import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useScrollToTop from '../../hooks/useScrollToTop';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faSpinner, faFlag, faComment, faFolder, faRobot, faTimes, faClock, faFlask, faList, faCalculator, faBookOpen } from '@fortawesome/free-solid-svg-icons';
import { debounce } from 'lodash';

// Import CSS for scaling
import './PracticeTest.css';

// Services imports
import examApi from '../../services/api/examApi';
import examSessionStorage from '../../services/examSessionStorage';
import questionFormatService from '../../services/questionFormatService';
import apiService from '../../services/apiService'; // Import apiService for notes API
import { 
  getLinkHref, 
  countCompletedQuestions, 
  calculateTimeSpent
} from '../../services/hateoasUtils';
import { 
  getDifficultyDisplayName, 
  getDifficultyId, 
  getAllDifficultyOptions 
} from '../../utils/difficultyMapping';
import { shouldPreventBackendUpdates } from '../../utils/examEndChecker';

import {
  prepareResponsePayload
} from './hateoas-utility-functions';

import { 
  ensureMinimalAnswersLinkOnQuestion,
  processQuestionsForExam 
} from '../../utils/questionUtils';

// Component imports
import QuestionNavigationSidebar from './QuestionNavigationSidebar';
import LabValueSidebar from './LabValueSidebar';
import ExamTimer from './ExamTimer';
import ProgressIndicator from './ProgressIndicator';
import SessionHeader from './SessionHeader';
import QuestionContent from './QuestionContent';
import CleanAnswerOptions from './CleanAnswerOptions';
import SessionSetupForm from './SessionSetupForm';
import DebugMonitor from './DebugMonitor'; // Added for debugging clues state
import FeedbackModal from './FeedbackModal';
import FolderManagementModal from './FolderManagementModal'; // Import the new modal
import QuestionAIChat from './QuestionAIChat'; // Import AI Chat component
import Calculator from './Calculator'; // Import Calculator component
import AssetDisplay from './AssetDisplay'; // Import Asset Display component
import CreateFlashcardModal from '../flashcards/CreateFlashcardModal'; // Import Create Flashcard modal
import userFolderService from '../../services/userFolderService'; // Import the folder service

// Timer service import
import examTimerService from '../../services/examTimerService';

// Styles imports
import '../PracticeTest.css';
import '../PracticeTest.responsive.css';
import '../PracticeTest.small-screen.css';
import '../PracticeTest.spinner.css';
import '../PracticeTest.answer-loading.css';
import '../custom-dropdown/enhanced-dropdown.css';
import './pinnable-navigation.css';

// Debugging utilities
// const debugParentRender = (componentId) => {
//   const renderCount = window._practiceTestRenderCount = (window._practiceTestRenderCount || 0) + 1;
//   const timestamp = new Date().toISOString();
//   console.log(`[PracticeTest][${componentId}] Parent component render #${renderCount} at ${timestamp}`);
//   return { renderCount, timestamp };
// };

// Debug utility for tracking API calls
const trackApiCall = (endpoint, operation) => {
  const callId = Math.random().toString(36).substring(2, 8);
  window._apiCallLog = window._apiCallLog || [];
  
  const callInfo = {
    id: callId,
    endpoint,
    operation,
    timestamp: new Date().toISOString(),
    stack: new Error().stack
  };
  
  window._apiCallLog.push(callInfo);
  // console.log(`[PracticeTest][API][${callId}] ${operation} to ${endpoint}`);
  
  return callId;
};


/**
 * PracticeTest component with HATEOAS API integration
 */
const PracticeTest = ({
  // Extract questions data from props
  questions: propsQuestions = [],
  responses: propsResponses = [],
  loading: propsLoading = false,
  error: propsError = null,
  totalQuestions: propsTotalQuestions = 0,
  // Exam data for URL navigation
  examData: propsExamData,
  setExamData: propsSetExamData,
  isDarkMode,
  themeStyles,
  setupComplete,
  sessionSetup,
  // Sidebar props
  isSidebarVisible = true,
  toggleSidebar,
  // Navigation props
  currentQuestionIndex = 0,
  handleNextQuestion,
  handlePreviousQuestion,
  handleGoToQuestion,
  ...props
}) => {
  // Ref for AI Chat (must be inside component)
  const aiChatRef = useRef(null);
  // Ref for question container (for resize handling)
  const questionContainerRef = useRef(null);
  // Generate a unique component ID for debugging
  const componentIdRef = useRef(Math.random().toString(36).substring(2, 8));
  
  // Debug: Log currentQuestionIndex from props
  // console.log(`🚨 [PracticeTest][${componentIdRef.current}] ═══ COMPONENT RENDER ═══`);
  // console.log(`� [PracticeTest][${componentIdRef.current}] �🔍 currentQuestionIndex from props: ${currentQuestionIndex}`);
  // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 📊 Total questions from props: ${propsTotalQuestions}`);
  // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 📋 Questions array length from props: ${propsQuestions?.length || 0}`);
  // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 🎯 setupComplete from props: ${setupComplete}`);
  // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 🔄 Loading state from props: ${propsLoading}`);
  // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 🕐 Render timestamp: ${new Date().toISOString()}`);
  // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 📍 Render stack:`);
  // console.trace(`🚨 [PracticeTest][${componentIdRef.current}] Component render origin`);
  
  // Track render for debugging
  // React Router hooks for URL management
  const navigate = useNavigate();
  const { id: urlExamId } = useParams();
  const [searchParams] = useSearchParams();
  const isResume = searchParams.get('resume') === 'true';
  
  // const renderInfo = debugParentRender(componentIdRef.current);
  
  // console.log(`[PracticeTest][${componentIdRef.current}] 🏗️ Component render - setupComplete: ${setupComplete}, isResume: ${isResume}`);
  
  // Log received props for debugging
  // console.log(`[PracticeTest][${componentIdRef.current}] 🔍 Received props:`, {
  //   examTypes: props.examTypes?.length || 0,
  //   userFolders: props.userFolders?.length || 0,
  //   isLoadingFolders: props.isLoadingFolders
  // });
  
  // Pinnable Navigation State (Desktop Only) - moved before useEffect to fix initialization order
  const [isNavigationPinned, setIsNavigationPinned] = useState(() => {
    // Pin navigation by default on desktop, unpin on mobile
    return window.innerWidth > 768;
  });
  
  // Handle window resize for responsive pinnable navigation
  useEffect(() => {
    const handleResize = () => {
      // Auto-unpin navigation on mobile
      if (window.innerWidth <= 768 && isNavigationPinned) {
        setIsNavigationPinned(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isNavigationPinned]);

  // Add global error handler for ResizeObserver loop errors
  useEffect(() => {
    const handleResizeObserverError = (event) => {
      // Suppress the specific ResizeObserver loop error that's harmless but noisy
      if (event.message && event.message.includes('ResizeObserver loop completed with undelivered notifications')) {
        event.preventDefault();
        event.stopPropagation();
        console.warn('[PracticeTest] Suppressed ResizeObserver loop error - this is a known harmless issue');
        return true;
      }
    };

    // Add error event listener
    window.addEventListener('error', handleResizeObserverError);
    
    return () => {
      window.removeEventListener('error', handleResizeObserverError);
    };
  }, []);

  // Track component lifecycle for debugging navigation issues
  useEffect(() => {
    // console.log(`[PracticeTest][${componentIdRef.current}] 🚀 COMPONENT MOUNTED - URL: ${window.location.href}, ExamId: ${urlExamId}, IsResume: ${isResume}, Timestamp: ${new Date().toISOString()}`);
    return () => {
      // console.log(`[PracticeTest][${componentIdRef.current}] 💥 COMPONENT UNMOUNTED - Timestamp: ${new Date().toISOString()}`);
      // Save session data when component unmounts (navigation away)
      if (examData && questions.length > 0) {
        examSessionStorage.saveExamSession(examData, examData?.id);
        examSessionStorage.saveQuestions(questions);
        examSessionStorage.saveResponses(responses);
        examSessionStorage.saveCurrentQuestionIndex(currentQuestionIndex, examData?.id || urlExamId);
        // console.log(`[PracticeTest][${componentIdRef.current}] 💾 Session data saved on unmount`);
      }
    };
  }, []);

  const [examData, setExamData] = useState(propsExamData || null);
  const [questions, setQuestions] = useState(propsQuestions);
  const [responses, setResponses] = useState(propsResponses || []);
  
  // Centralized response state - single source of truth for all question states
  const [examResponses, setExamResponses] = useState({});
  
  // Track setup complete locally if not provided via props
  const [localSetupComplete, setLocalSetupComplete] = useState(false);
  
  const [localSessionSetup, setSessionSetup] = useState(sessionSetup || {
    title: '',
    examType: '',
    system: [],
    discipline: [],
    difficulty: ['ALL'],
    questionCount: 10,
    filterOption: '',
    userFolderIds: []
  });
    // Use currentQuestionIndex from props (managed by container)
  useScrollToTop(currentQuestionIndex);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentAnswers, setCurrentAnswers] = useState([]);
  const [currentResponse, setCurrentResponse] = useState(null);
  
  // Helper function to update examData - uses prop function when available
  const updateExamData = (data) => {
    if (propsSetExamData) {
      propsSetExamData(data);
    } else {
      setExamData(data);
    }
  };
    // UI state - using isAnswerSubmitted as the single source of truth
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswerSubmitted, setIsAnswerSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [isFlagged, setIsFlagged] = useState(false);
  
  // Refs and triggers for coordinated button behavior
  const cleanAnswerOptionsRef = useRef(null);
  const endSessionBtnRef = useRef(null);
  const [submitTrigger, setSubmitTrigger] = useState(0);
      // Application state
  const [isLoading, setIsLoading] = useState(propsLoading);
  const [isSessionEnding, setIsSessionEnding] = useState(false);
  const [error, setError] = useState(propsError);  
  // UI interaction controls
  const [showClues, setShowClues] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showCreateFlashcardModal, setShowCreateFlashcardModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false); // State for folder modal
  const [showEndSessionModal, setShowEndSessionModal] = useState(false); // State for end session confirmation modal

  // AI Chat state management
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiChatState, setAiChatState] = useState({
    isOpen: false,
    isMinimized: false,
    isConnected: false
  });

  // Lab Value Sidebar state
  const [showLabValueSidebar, setShowLabValueSidebar] = useState(false);
  const [labValueSISColumn, setLabValueSISColumn] = useState(false);

  // Track lab value sidebar state changes
  useEffect(() => {
    // console.log('[LabValueSidebar] State changed:', {
    //   showLabValueSidebar,
    //   timestamp: new Date().toISOString(),
    //   questionContainerRect: questionContainerRef.current?.getBoundingClientRect(),
    //   questionContainerStyle: questionContainerRef.current ? {
    //     left: questionContainerRef.current.style.left,
    //     width: questionContainerRef.current.style.width,
    //     position: questionContainerRef.current.style.position
    //   } : null
    // });
  }, [showLabValueSidebar]);

  // Handle SI column state changes from LabValueSidebar
  const handleSIColumnChange = useCallback((showSIColumn) => {
    setLabValueSISColumn(showSIColumn);
  }, []);

  // Wrapped toggleSidebar function that ensures only one sidebar is open
  const handleToggleSidebar = useCallback(() => {
    // If opening Question Navigation (currently closed), close Lab Values first
    if (!isSidebarVisible && showLabValueSidebar) {
      setShowLabValueSidebar(false);
    }
    // Then toggle the Question Navigation
    toggleSidebar();
  }, [isSidebarVisible, showLabValueSidebar, toggleSidebar]);

  // Debug resize handle positioning
  useEffect(() => {
    const checkResizeHandle = () => {
      const container = document.getElementById('examflip-question-container');
      const handle = container?.querySelector('.resize-handle');
      
      if (handle) {
        // Check DOM hierarchy
        let current = handle;
        let depth = 0;
        while (current && depth < 10) {
          current = current.parentElement;
          depth++;
        }
        
        const rect = handle.getBoundingClientRect();
      }
    };
    
    // Check immediately and after a short delay
    checkResizeHandle();
    const timeoutId = setTimeout(checkResizeHandle, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [isSidebarVisible]); // Re-check when sidebar visibility changes

  // Folder Management State
  const [userFolders, setUserFolders] = useState([]);
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [folderError, setFolderError] = useState(null);
  const [currentQuestionFolderIds, setCurrentQuestionFolderIds] = useState([]); // <-- Fix: add this state

  // Question Notes State
  const [showNotes, setShowNotes] = useState(false);
  const [currentQuestionNote, setCurrentQuestionNote] = useState('');
  const [isLoadingNote, setIsLoadingNote] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteError, setNoteError] = useState(null);
  const [noteLastSaved, setNoteLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Question Assets State
  const [currentQuestionAssets, setCurrentQuestionAssets] = useState([]);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [assetsError, setAssetsError] = useState(null);

  // Plan Limit State
  const [showPlanLimitBanner, setShowPlanLimitBanner] = useState(false);
  const [planLimitData, setPlanLimitData] = useState(null);
  const [isCheckingPlanLimits, setIsCheckingPlanLimits] = useState(false);

  // Resizable Question Container State
  const [questionContainerWidth, setQuestionContainerWidth] = useState(() => {
    const saved = localStorage.getItem('examflip-question-container-width');
    return saved ? parseInt(saved, 10) : null;
  });

  // Ref to track viewport dimensions for zoom/resize detection without triggering re-renders
  const lastViewportStateRef = useRef({
    width: window.innerWidth,
    height: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio || 1
  });

  // Apply the saved question container width (if any) using the DOM API so we can set priority '!important'.
  useEffect(() => {
    // console.log('[PracticeTest] useEffect triggered for width application');
    const el = questionContainerRef.current;
    const STYLE_ID = 'examflip-question-width-style';
    const CONTAINER_ID = 'examflip-question-container';

    const applyWidth = () => {
      const raw = localStorage.getItem('examflip-question-container-width');
      const stored = raw ? parseInt(raw, 10) : null;

      // console.log('[PracticeTest] applyWidth called. raw from localStorage:', raw, 'parsed stored:', stored, 'window.innerWidth:', window.innerWidth);

      // Always try to find the element by id, since ref might be null if not rendered yet
      const target = document.getElementById(CONTAINER_ID);
      if (!target) {
        // console.log('[PracticeTest] target element not found by id, will retry in 100ms');
        setTimeout(applyWidth, 100); // Retry after a short delay
        return false; // signal not applied
      }

      // console.log('[PracticeTest] target element found by id:', target);

      // assign an id to the element so we can target it specifically (if not already)
      if (!target.id) target.id = CONTAINER_ID;

      let styleEl = document.getElementById(STYLE_ID);
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = STYLE_ID;
        document.head.appendChild(styleEl);
        // console.log('[PracticeTest] Created new style element');
      }

      if (stored) {
        // Clamp the stored width so it never exceeds the viewport minus a small margin
        const margin = 40; // leave some breathing room
        const maxAllowed = Math.max(300, window.innerWidth - margin); // ensure a sensible minimum
        const applied = Math.min(stored, maxAllowed);

        // console.log('[PracticeTest] Clamping: stored=', stored, 'maxAllowed=', maxAllowed, 'applied=', applied);

        // Always apply the clamped stored width, even on smaller screens, but ensure it's at least 300px
        if (applied >= 300) {
          const css = `#${CONTAINER_ID} { max-width: ${applied}px !important; width: ${applied}px !important; }`;
          styleEl.textContent = css;
          // console.log('[PracticeTest] Applied stored width:', css);
        } else {
          // If clamped to less than 300, use 100% to avoid too narrow
          styleEl.textContent = `#${CONTAINER_ID} { max-width: 100% !important; width: 100% !important; }`;
          // console.log('[PracticeTest] Applied too small, using 100% fallback');
        }
      } else {
        // fallback
        styleEl.textContent = `#${CONTAINER_ID} { max-width: 100% !important; }`;
        // console.log('[PracticeTest] No stored width, using fallback: max-width:100%');
      }

      return true; // applied
    };

    const resetToResponsiveWidth = () => {
      const STYLE_ID = 'examflip-question-width-style';
      const styleEl = document.getElementById(STYLE_ID);
      if (styleEl) {
        styleEl.textContent = `#${CONTAINER_ID} { max-width: 100% !important; width: auto !important; }`;
      }
      
      // Clear the stored width from localStorage
      localStorage.removeItem('examflip-question-container-width');
      setQuestionContainerWidth(null);
      
      console.log('[PracticeTest] Reset question container to responsive width due to significant viewport change');
    };

    const handleViewportChange = debounce(() => {
      const currentState = {
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio || 1
      };

      // Calculate percentage change in viewport width
      const widthChangePercent = Math.abs(currentState.width - lastViewportStateRef.current.width) / lastViewportStateRef.current.width;
      
      // Check for significant changes that might indicate zoom
      const significantWidthChange = widthChangePercent > 0.15; // 15% change
      const devicePixelRatioChanged = Math.abs(currentState.devicePixelRatio - lastViewportStateRef.current.devicePixelRatio) > 0.1;

      if (significantWidthChange || devicePixelRatioChanged) {
        // console.log('[PracticeTest] Significant viewport change detected:', {
        //   oldState: lastViewportStateRef.current,
        //   newState: currentState,
        //   widthChangePercent: (widthChangePercent * 100).toFixed(1) + '%',
        //   resetting: true
        // });
        
        resetToResponsiveWidth();
      } else {
        // Normal resize, just apply clamping
        applyWidth();
      }

      // Update the ref (this won't trigger re-renders)
      lastViewportStateRef.current = currentState;
    }, 100); // Debounce resize events to prevent excessive calls

    // Try applying immediately; if the element isn't available yet, retry a few times
    let attempts = 0;
    const maxAttempts = 10; // Increase attempts

    const tryApply = () => {
      attempts += 1;
      // console.log('[PracticeTest] tryApply attempt', attempts);
      const ok = applyWidth();
      if (!ok && attempts < maxAttempts) {
        // retry on next animation frame
        requestAnimationFrame(tryApply);
      }
    };

    tryApply();
    
    // Use the new resize handler that can detect zoom changes
    window.addEventListener('resize', handleViewportChange);
    return () => {
      window.removeEventListener('resize', handleViewportChange);
      // Cancel any pending debounced calls
      handleViewportChange.cancel();
    };
  }, [questionContainerWidth, isSidebarVisible, questions.length]);

  // Mobile detection for AI button text
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 769);

  // Ensure end session button keeps its intended color even when global CSS overrides are present
  useEffect(() => {
    const el = endSessionBtnRef.current;
    if (!el) return;

    const applyInlineStyles = () => {
      if (isDarkMode) {
        el.style.setProperty('background', 'rgba(239, 68, 68, 0.15)', 'important');
        el.style.setProperty('border', '1px solid rgba(239, 68, 68, 0.5)', 'important');
        el.style.setProperty('color', '#fca5a5', 'important');
      } else {
        // Strong light-mode red with good contrast; use darker shade for text
        el.style.setProperty('background', 'rgba(220, 38, 38, 0.06)', 'important');
        el.style.setProperty('border', '1px solid rgba(220, 38, 38, 0.25)', 'important');
        el.style.setProperty('color', '#b91c1c', 'important');
      }

      // Common layout styles (reapplied inline to avoid global overrides)
      el.style.setProperty('padding', '8px 12px', 'important');
      el.style.setProperty('font-size', '13px', 'important');
      el.style.setProperty('font-weight', '500', 'important');
      el.style.setProperty('border-radius', '6px', 'important');
      el.style.setProperty('display', 'flex', 'important');
      el.style.setProperty('align-items', 'center', 'important');
      el.style.setProperty('gap', '8px', 'important');
    };

    applyInlineStyles();

    // Reapply when theme toggles
    return () => {
      // no cleanup needed for inline styles
    };
  }, [isDarkMode]);

  // Ref for auto-save timeout
  const autoSaveTimeoutRef = useRef(null);

  // Exam timer state
  const [examTimerRunning, setExamTimerRunning] = useState(false);
  const timerDisplayRef = useRef(0); // Ref to track last displayed time
  const timerElementRef = useRef(null); // Ref to timer display element
  const handleExamTimerUpdate = useCallback(async (timeSpent) => {
    if (!examData || !examData._links) {
      // console.log(`[PracticeTest][${componentIdRef.current}] No exam data or links available for timer update`);
      return;
    }    // Don't send time updates if exam is finished or has ended
    if (shouldPreventBackendUpdates(examData)) {
      // console.log(`[PracticeTest][${componentIdRef.current}] Exam has ended or is finished, skipping timer update`);
      return;
    }

    try {
      const updateUrl = getLinkHref(examData._links, 'update');
      if (!updateUrl) {
        // console.log(`[PracticeTest][${componentIdRef.current}] No update link found in exam data`);
        return;
      }

      const updatePayload = {
        TimeSpent: timeSpent
      };

      // console.log(`[PracticeTest][${componentIdRef.current}] Sending exam timer update:`, {
      //   url: updateUrl,
      //   timeSpent,
      //   payload: updatePayload
      // });      
      const callId = trackApiCall(updateUrl, 'PATCH_EXAM_TIMER_UPDATE');
      const response = await examApi.followHateoasLink(updateUrl, 'POST', updatePayload);
      // console.log(`[PracticeTest][${componentIdRef.current}][${callId}] Exam timer update successful:`, response);
        } catch (error) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error updating exam timer:`, error);
    }  }, [examData]);
    
  // Dropdown state
  const [isSystemDropdownOpen, setIsSystemDropdownOpen] = useState(false);
  const [isDisciplineDropdownOpen, setIsDisciplineDropdownOpen] = useState(false);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isExamTypeDropdownOpen, setIsExamTypeDropdownOpen] = useState(false);
  const [isDifficultyDropdownOpen, setIsDifficultyDropdownOpen] = useState(false);
  const [isUserFolderDropdownOpen, setIsUserFolderDropdownOpen] = useState(false);
      
  // Sidebar pagination - responsive for mobile
  const QUESTIONS_PER_PAGE = window.innerWidth <= 768 ? 10 : 15; // More questions on mobile
  const [sidebarPage, setSidebarPage] = useState(0);   
  const totalSidebarPages = Math.max(1, Math.ceil(questions.length / QUESTIONS_PER_PAGE));
  
  // Constants for dropdowns  
  const filterOptions = [
    "None",
    "Questions I have never seen",
    "Questions I have answered incorrectly",
    "Questions I have answered correctly",
    "Questions I have seen before"
  ];

  // Centralized response state management - moved before handleAnswerSubmitted to fix initialization order
  const updateExamResponse = useCallback((questionId, responseData) => {
    // console.log(`[PracticeTest][${componentIdRef.current}] Updating centralized response for question ${questionId}:`, responseData);
    
    setExamResponses(prev => {
      const updated = {
        ...prev,
        [questionId]: {
          questionId,
          answerIds: responseData.answerIds || [],
          isCorrect: responseData.isCorrect || false,
          isSkipped: responseData.isSkipped || false,
          isFlagged: responseData.isFlagged || false,
          timeSpent: responseData.timeSpent || 0,
          timestamp: responseData.timestamp || new Date().toISOString()
        }
      };
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Updated examResponses:`, updated);
      return updated;
    });
  }, []);

  // Get current question response from centralized state
  const getCurrentQuestionResponse = useCallback((questionId) => {
    return examResponses[questionId] || null;
  }, [examResponses]);  // Check if current question is locked (has been answered/skipped)
  const isCurrentQuestionLocked = useCallback((questionId) => {
    const response = examResponses[questionId];
    
    // A question is locked ONLY if it has been answered (has answerIds)
    // Skipped questions should remain unlocked so they can be answered later
    const hasAnswers = response?.answerIds && response.answerIds.length > 0;
    const isLocked = !!(response && hasAnswers);
    
    // console.log(`[PracticeTest][${componentIdRef.current}] isCurrentQuestionLocked check:`, {
    //   questionId,
    //   hasResponse: !!response,
    //   hasAnswers,
    //   isSkipped: response?.isSkipped,
    //   isLocked,
    //   answerIds: response?.answerIds || 'none',
    //   responseData: response || 'none',
    //   totalResponses: Object.keys(examResponses).length,
    //   timestamp: new Date().toISOString()
    // });
    
    return isLocked;
  }, [examResponses]);

  // Initialize examResponses from responses array
  const initializeExamResponses = useCallback((responsesArray) => {
    const responseMap = {};
    responsesArray.forEach(response => {
      responseMap[response.questionId] = {
        questionId: response.questionId,
        answerIds: response.answerIds || [],
        isCorrect: response.isCorrect || false,
        isSkipped: response.isSkipped || false,
        isFlagged: response.isFlagged || false,
        timeSpent: response.timeSpent || 0,
        timestamp: response.timestamp || new Date().toISOString()
      };
    });
    
    // console.log(`[PracticeTest][${componentIdRef.current}] Initialized examResponses from ${responsesArray.length} responses:`, responseMap);
    setExamResponses(responseMap);
  }, []);  // Hydrate current question state from centralized responses
  const hydrateCurrentQuestionState = useCallback((questionId, answersData = [], directResponses = null) => {
    // Use direct responses if provided (for resume), otherwise use state
    const responsesToUse = directResponses || examResponses;
    const responseData = responsesToUse[questionId];
    
    // console.log(`[PracticeTest][${componentIdRef.current}] 🔄 HYDRATING QUESTION ${questionId}:`, {
    //   hasResponse: !!responseData,
    //   answerIds: responseData?.answerIds?.length || 0,
    //   isSkipped: responseData?.isSkipped,
    //   answersCount: answersData.length
    // });
    
    if (!responseData || (!responseData.answerIds || responseData.answerIds.length === 0) && !responseData.isSkipped) {
      // No response OR response exists but no answers and not skipped - reset to blank state (question should be editable)
      setSelectedAnswer(null);
      setIsAnswerSubmitted(false);
      setIsCorrect(false);
      setIsFlagged(responseData?.isFlagged || false);
      // console.log(`[PracticeTest][${componentIdRef.current}] No answered/skipped response - resetting to blank state`);
      return;
    }
    
    // Set flag state
    setIsFlagged(responseData.isFlagged || false);
    
    // Handle answered questions
    if (responseData.answerIds && responseData.answerIds.length > 0 && answersData.length > 0) {
      const answerId = responseData.answerIds[0];
      const answerIndex = answersData.findIndex(a => a.id === answerId);
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Looking for answer ID "${answerId}" in ${answersData.length} answers`);
      
      if (answerIndex >= 0) {
        const answerLetter = String.fromCharCode(65 + answerIndex);
        setSelectedAnswer(answerLetter);
        setIsAnswerSubmitted(true);
        setIsCorrect(responseData.isCorrect);
        
        // console.log(`[PracticeTest][${componentIdRef.current}] ✅ HYDRATED: answer ${answerLetter}, correct: ${responseData.isCorrect}`);
      } else {
        // console.log(`[PracticeTest][${componentIdRef.current}] ❌ ANSWER NOT FOUND: ID ${answerId} not in answers`);
      }
    } else if (responseData.isSkipped) {
      // Response exists and is marked as skipped
      setSelectedAnswer(null);
      setIsAnswerSubmitted(true);
      setIsCorrect(responseData.isCorrect || false);
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Hydrated skipped question:`, {
      //   isCorrect: responseData.isCorrect
      // });
    }
  }, [examResponses]);
  
  // Handle answer submission from CleanAnswerOptions
  const handleAnswerSubmitted = useCallback((responseData) => {
    // console.log(`[PracticeTest][${componentIdRef.current}] ====== handleAnswerSubmitted CALLBACK TRIGGERED ======`);
    // console.log(`[PracticeTest][${componentIdRef.current}] Answer submitted callback triggered:`, {
    //   responseData,
    //   questionId: currentQuestion?.id,
    //   selectedAnswer,
    //   previousIsAnswerSubmitted: isAnswerSubmitted,
    //   currentResponsesLength: responses.length,
    //   timestamp: new Date().toISOString()
    // });
      // Handle backward compatibility: if responseData is just a boolean, convert it
    let processedResponse;
    if (typeof responseData === 'boolean') {
      // Legacy format - create a minimal response object
      // Convert answer letter to actual answer ID for proper storage
      let actualAnswerIds = [];
      if (selectedAnswer && currentAnswers && currentAnswers.length > 0) {
        const selectedIndex = selectedAnswer.charCodeAt(0) - 65; // Convert A->0, B->1, etc.
        if (selectedIndex >= 0 && selectedIndex < currentAnswers.length) {
          actualAnswerIds = [currentAnswers[selectedIndex].id];
          // console.log(`[PracticeTest][${componentIdRef.current}] Legacy format: Converting answer letter "${selectedAnswer}" to ID:`, actualAnswerIds[0]);
        }
      }
      
      processedResponse = {
        questionId: currentQuestion?.id,
        isCorrect: responseData,
        answerIds: actualAnswerIds,
        timeSpent: 0 // Default value for legacy calls
      };
      // console.log(`[PracticeTest][${componentIdRef.current}] Legacy boolean response converted to:`, processedResponse);
    } else {
      // New format - use the complete response object
      processedResponse = {
        questionId: currentQuestion?.id,
        isCorrect: responseData?.isCorrect || false,
        answerIds: responseData?.answerIds || [],
        timeSpent: responseData?.timeSpent || 0,
        isFlagged: responseData?.isFlagged || isFlagged || false,
        isSkipped: responseData?.isSkipped || false,
        timestamp: responseData?.timestamp || new Date().toISOString()
      };
      // console.log(`[PracticeTest][${componentIdRef.current}] Complete response object processed:`, processedResponse);
    }
    
    // Update centralized response state FIRST
    updateExamResponse(currentQuestion?.id, processedResponse);
    
    // Update the parent UI state
    // console.log(`[PracticeTest][${componentIdRef.current}] Setting isCorrect to:`, processedResponse.isCorrect);
    setIsCorrect(processedResponse.isCorrect);
    
    // console.log(`[PracticeTest][${componentIdRef.current}] Setting isAnswerSubmitted to: true`);
    setIsAnswerSubmitted(true);
    
    // Update isFlagged state if it changed
    if (processedResponse.isFlagged !== isFlagged) {
      // console.log(`[PracticeTest][${componentIdRef.current}] Updating flag state to:`, processedResponse.isFlagged);
      setIsFlagged(processedResponse.isFlagged);
    }
    
    // Merge the response into the responses array (for backward compatibility)
    // console.log(`[PracticeTest][${componentIdRef.current}] ===== MERGING RESPONSE INTO RESPONSES ARRAY =====`);
    // console.log(`[PracticeTest][${componentIdRef.current}] Before merge - responses array:`, {
    //   length: responses.length,
    //   questionIds: responses.map(r => r.questionId),
    //   targetQuestionId: processedResponse.questionId
    // });
    
    setResponses(prevResponses => {
      const updatedResponses = [...prevResponses];
      
      // Find existing response for this question
      const existingIndex = updatedResponses.findIndex(r => r.questionId === processedResponse.questionId);
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Existing response index: ${existingIndex}`);
      
      if (existingIndex >= 0) {
        // Update existing response
        const oldResponse = updatedResponses[existingIndex];
        updatedResponses[existingIndex] = { ...updatedResponses[existingIndex], ...processedResponse };
        // console.log(`[PracticeTest][${componentIdRef.current}] Updated existing response at index ${existingIndex}:`, {
        //   old: oldResponse,
        //   new: updatedResponses[existingIndex]
        // });
      } else {
        // Add new response
        updatedResponses.push(processedResponse);
        // console.log(`[PracticeTest][${componentIdRef.current}] Added new response:`, processedResponse);
      }
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Updated responses array:`, {
      //   length: updatedResponses.length,
      //   questionIds: updatedResponses.map(r => r.questionId),
      //   newResponse: processedResponse
      // });
      
      // Save to session storage for persistence
      examSessionStorage.saveResponses(updatedResponses);
      
      return updatedResponses;
    });
    
    // console.log(`[PracticeTest][${componentIdRef.current}] Answer submission state updated successfully`);
    // console.log(`[PracticeTest][${componentIdRef.current}] ====== handleAnswerSubmitted CALLBACK COMPLETED ======`);
  }, [currentQuestion?.id, selectedAnswer, isAnswerSubmitted, isFlagged, responses.length, updateExamResponse]);  // Handle answers loaded from EnhancedAnswerOptions
  const handleAnswersLoaded = useCallback((answers) => {
    // console.log(`[PracticeTest][${componentIdRef.current}] Answers loaded from child component:`, {
    //   count: answers?.length || 0,
    //   questionId: currentQuestion?.id
    // });
    
    // Update the parent's currentAnswers state with the answers from child component
    setCurrentAnswers(answers || []);
    
    // Trigger hydration for the current question now that we have answers
    if (currentQuestion?.id && answers && answers.length > 0) {
      // console.log(`[PracticeTest][${componentIdRef.current}] Triggering hydration after answers loaded`);
      hydrateCurrentQuestionState(currentQuestion.id, answers);
    }
  }, [currentQuestion?.id, hydrateCurrentQuestionState]);
  // Helper function to hydrate state from existing response
  const hydrateFromResponse = useCallback((questionId, answersData = []) => {
    const saved = responses.find(r => r.questionId === questionId);
    
    // console.log(`[PracticeTest][${componentIdRef.current}] hydrateFromResponse called:`, {
    //   questionId,
    //   hasResponse: !!saved,
    //   responseData: saved,
    //   answersDataLength: answersData.length
    // });
    
    if (!saved) {
      // No saved response - reset to blank state
      setSelectedAnswer(null);
      setIsAnswerSubmitted(false);
      setIsCorrect(false);
      // console.log(`[PracticeTest][${componentIdRef.current}] No saved response - resetting to blank state`);
      return;
    }
    
    // Has saved response - restore the state
    if (saved.answerIds && saved.answerIds.length > 0 && answersData.length > 0) {
      // Find the answer object from the first answer ID
      const answerId = saved.answerIds[0];
      const answerIndex = answersData.findIndex(a => a.id === answerId);
      
      if (answerIndex >= 0) {
        const answerLetter = String.fromCharCode(65 + answerIndex);
        setSelectedAnswer(answerLetter);
        setIsAnswerSubmitted(true);
        setIsCorrect(saved.isCorrect);
        
        // console.log(`[PracticeTest][${componentIdRef.current}] Restored state from response:`, {
        //   answerId,
        //   answerIndex,
        //   answerLetter,
        //   isCorrect: saved.isCorrect
        // });
      } else {
        // console.log(`[PracticeTest][${componentIdRef.current}] Answer ID not found in answers data:`, {
        //   answerId,
        //   availableAnswerIds: answersData.map(a => a.id)
        // });
      }
    } else {
      // Response exists but no answer selected (skipped question)
      setSelectedAnswer(null);
      setIsAnswerSubmitted(true);
      setIsCorrect(saved.isCorrect);
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Restored skipped question state:`, {
      //   isCorrect: saved.isCorrect
      // }); 
       }  }, [responses]);

  // Safeguard: Debounce loadQuestion to prevent rapid calls - MOVED UP to fix initialization order
  const debouncedLoadQuestion = useCallback(
    debounce(async (question, allResponses = []) => {
        if (!question) return;
        try {
          setIsLoading(true);
          
          // Reset clues state FIRST, before any other operations
          // This ensures that when the component rerenders with the new question,
          // clues will be hidden regardless of any race conditions
          // console.log(`[PracticeTest][${componentIdRef.current}] Setting showClues to false - current state:`, showClues);
          
          // Force immediate state update to false - this is crucial
          setShowClues(false);
          
// Check for clues in either bodyFormatted or body properties
const hasCluesInBodyFormatted = question.bodyFormatted && 
  (question.bodyFormatted.includes('<clue>') || question.bodyFormatted.includes('['));
const hasCluesInBody = question.body && 
  (question.body.includes('<clue>') || question.body.includes('['));
const hasFormattedClues = hasCluesInBodyFormatted || hasCluesInBody;

// Create formatted versions with and without clues to ensure consistency
const formattedWithClues = questionFormatService.getQuestionWithClues(
  hasCluesInBodyFormatted ? question : {...question, bodyFormatted: question.body}, 
  true
);

const formattedWithoutClues = questionFormatService.getQuestionWithClues(
            hasCluesInBodyFormatted ? question : {...question, bodyFormatted: question.body},
            false
          );
          
          // Add the formatted versions to the question object
          const enhancedQuestion = {
            ...question,
            formattedWithClues,
            formattedWithoutClues,
            hasClues: hasFormattedClues
          };
          
          // console.log(`[PracticeTest][${componentIdRef.current}] Enhanced question with formatted content:`, {
          //   questionId: enhancedQuestion.id,
          //   hasFormattedClues,
          //   formattedWithCluesLength: formattedWithClues.length,
          //   formattedWithoutCluesLength: formattedWithoutClues.length
          // });
          
          // console.log(`[PracticeTest][${componentIdRef.current}] Setting currentQuestion, reference ID:`, Object.prototype.toString.call(enhancedQuestion));
          
          // Store question in state
          setCurrentQuestion(enhancedQuestion);
            
          // Ensure allResponses is an array even if it's undefined
          const safeResponses = Array.isArray(allResponses) ? allResponses : [];
          
          // Find existing response for this question
          const existingResponse = safeResponses.find(r => r.questionId === question.id);
          setCurrentResponse(existingResponse || null);
          
          // console.log(`[PracticeTest][${componentIdRef.current}] debouncedLoadQuestion - existingResponse check:`, {
          //   questionId: question.id,
          //   hasExistingResponse: !!existingResponse,
          //   existingResponse: existingResponse,
          //   currentSelectedAnswer: selectedAnswer,
          //   currentIsAnswerSubmitted: isAnswerSubmitted,
          //   currentIsCorrect: isCorrect
          // });
        } catch (err) {
         // // console.error(`[PracticeTest][${componentIdRef.current}] Error in debouncedLoadQuestion:`, err);
          setError(`Failed to load question: ${err.message}`);
        } finally {
          setIsLoading(false);
        }
    }, 300),
    [selectedAnswer, isAnswerSubmitted, isCorrect, showClues]
  );

  // Sync questions from props to local state
  useEffect(() => {
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] ═══ QUESTIONS SYNC USEEFFECT TRIGGERED ═══`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 📊 Props questions length: ${propsQuestions?.length || 0}`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 📊 Current local questions length: ${questions.length}`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 🔄 Props loading state: ${propsLoading}`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] ❌ Props error state: ${propsError}`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 🎯 Setup complete: ${setupComplete}`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 🎯 Local setup complete: ${localSetupComplete}`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 🕐 Sync timestamp: ${new Date().toISOString()}`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 📍 Stack trace:`);
    // console.trace(`🚨 [PracticeTest][${componentIdRef.current}] Questions sync origin`);
    
    // Update questions when props change
    if (propsQuestions && propsQuestions.length > 0) {
      // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 🔄 UPDATING QUESTIONS FROM PROPS`);
      // Sort questions by ID before updating state

      setQuestions(propsQuestions);
    }
    
    // Update loading and error states from props
    setIsLoading(propsLoading);
    if (propsError) {
      setError(propsError);
    }
  }, [propsQuestions, propsLoading, propsError, setupComplete, localSetupComplete, currentQuestion, responses]);
  
  // Auto-load first question when both questions and responses are ready (for exam resume scenarios)
  useEffect(() => {
    // Load the first question automatically when questions are loaded from URL and responses are initialized
    // This handles the case where exam is loaded from URL parameter during resume
    if ((setupComplete || localSetupComplete) && 
        !propsLoading && 
        !currentQuestion && 
        propsQuestions && propsQuestions.length > 0 &&
        Object.keys(examResponses).length >= 0) { // examResponses initialized (could be empty for new exams)
      
      // Check if there's a saved current question index from session storage
      const savedIndex = examSessionStorage.getCurrentQuestionIndex(examData?.id || urlExamId);
      const questionIndex = (savedIndex >= 0 && savedIndex < propsQuestions.length) ? savedIndex : 0;
      const questionToLoad = propsQuestions[questionIndex];
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Auto-loading question from URL-loaded exam:`, {
      //   questionsLength: propsQuestions.length,
      //   responsesLength: responses?.length || 0,
      //   examResponsesKeys: Object.keys(examResponses).length,
      //   savedIndex,
      //   questionIndex,
      //   questionId: questionToLoad?.id
      // });
      
      // Update the current question index state
      if (handleGoToQuestion) {
        handleGoToQuestion(questionIndex);
      }
      
      debouncedLoadQuestion(questionToLoad, responses || []);
    }
  }, [propsQuestions, propsLoading, setupComplete, localSetupComplete, currentQuestion, responses, examResponses, debouncedLoadQuestion]);
  // Sync responses from props and initialize examResponses
  useEffect(() => {
    // console.log(`[PracticeTest][${componentIdRef.current}] Syncing responses from props:`, {
    //   propsResponsesLength: propsResponses?.length || 0,
    //   currentResponsesLength: responses.length,
    //   timestamp: new Date().toISOString()
    // });
    
    // Update responses when props change (important for resume scenarios)
    if (propsResponses && propsResponses.length > 0) {
      setResponses(propsResponses);
      // console.log(`[PracticeTest][${componentIdRef.current}] Updated responses from props:`, {
      //   newLength: propsResponses.length,
      //   responseQuestionIds: propsResponses.map(r => r.questionId),
      //   timestamp: new Date().toISOString()
      // });
      
      // Initialize centralized examResponses state from the props responses
      // console.log(`[PracticeTest][${componentIdRef.current}] Initializing examResponses from props responses`);
      initializeExamResponses(propsResponses);
    }
  }, [propsResponses, initializeExamResponses]);

  // Re-hydrate current question state when examResponses changes and we have a current question
  // This fixes the issue where first question's previous answer state doesn't show on exam resume
  useEffect(() => {
    // console.log(`[PracticeTest][${componentIdRef.current}] examResponses changed - checking if current question needs re-hydration:`, {
    //   hasCurrentQuestion: !!currentQuestion,
    //   currentQuestionId: currentQuestion?.id,
    //   hasAnswers: currentAnswers?.length > 0,
    //   examResponsesKeys: Object.keys(examResponses),
    //   hasResponseForCurrentQuestion: !!examResponses[currentQuestion?.id]
    // });
    
    // If we have a current question, answers, and the examResponses state just got initialized/updated
    // This handles the case where the first question is answered and then the exam is resumed
    if (currentQuestion && currentAnswers && currentAnswers.length > 0 && Object.keys(examResponses).length > 0) {
      const responseForCurrentQuestion = examResponses[currentQuestion.id];
      
      if (responseForCurrentQuestion) {
        // console.log(`[PracticeTest][${componentIdRef.current}] Re-hydrating current question state after examResponses update:`, {
        //   questionId: currentQuestion.id,
        //   responseData: responseForCurrentQuestion,
        //   answersCount: currentAnswers.length
        // });
        
        // Re-hydrate the current question state with the proper response data
        hydrateCurrentQuestionState(currentQuestion.id, currentAnswers);
      }
    }
  }, [examResponses, currentQuestion?.id, currentAnswers?.length, hydrateCurrentQuestionState]);

  const systems = [
    "ALL",
    "Behavioral Health",
    "Blood & Lymphoreticular Systems",
    "Cardiovascular Systems",
    "Endocrine System",
    "Gastrointestinal System",
    "Musculoskeletal Systems",
    "Nervous System & Special Senses",
    "Reproductive System",
    "Respiratory System",
    "Skin & Subcutaneous Tissue"
  ];  const difficulties = [
    "ALL",
    ...getAllDifficultyOptions().map(option => option.name)
  ];

  // Start session handler
  const handleStartSession = useCallback(async () => {
    // console.log(`[PracticeTest][${componentIdRef.current}] ===== HANDLE START SESSION CALLED =====`);
    // console.log(`[PracticeTest][${componentIdRef.current}] isLoading:`, isLoading);
    // console.log(`[PracticeTest][${componentIdRef.current}] props.handleStartSession:`, !!props.handleStartSession);
    // console.log(`[PracticeTest][${componentIdRef.current}] props.onStartSession:`, !!props.onStartSession);
    // console.log(`[PracticeTest][${componentIdRef.current}] sessionSetup:`, sessionSetup);
    // console.log(`[PracticeTest][${componentIdRef.current}] localSessionSetup:`, localSessionSetup);
    
    if (isLoading) {
      // console.log(`[PracticeTest][${componentIdRef.current}] Already loading, returning early`);
      return; // Prevent multiple calls while loading
    }
    
    setIsLoading(true);
    try {
      const currentSetup = sessionSetup || localSessionSetup;
      // console.log(`[PracticeTest][${componentIdRef.current}] Using setup:`, currentSetup);
      
      // Validate that session title is not empty
      if (!currentSetup.title || currentSetup.title.trim() === '') {
        // console.log(`[PracticeTest][${componentIdRef.current}] Title validation failed:`, currentSetup.title);
        // Use component UI error area instead of browser alert so message appears inline in the setup form
        setError('Session title is required. Please enter a title for your practice session.');
        setIsLoading(false); // Reset loading state before returning
        return;
      }
      
      // Validate that there are questions available
      if (props.availableQuestionsCount === 0) {
        // console.log(`[PracticeTest][${componentIdRef.current}] No questions available:`, props.availableQuestionsCount);
        setError('No questions available for the selected criteria. Please adjust your filters or selection.');
        setIsLoading(false); // Reset loading state before returning
        return;
      }
      
      // First check if we have a handleStartSession prop from the container
      if (props.handleStartSession) {
        // console.log(`[PracticeTest][${componentIdRef.current}] Using props.handleStartSession`);
        await props.handleStartSession();
        return;
      }
      
      if (props.onStartSession) {
        // console.log(`[PracticeTest][${componentIdRef.current}] Using props.onStartSession`);
        props.onStartSession();
      } else {
        // console.log(`[PracticeTest][${componentIdRef.current}] No props handler, implementing locally`);
        // Since onStartSession prop is not provided, we need to implement the functionality here
        setError(null);
        
        // Clear any previous exam session data to prevent conflicts with resume logic
        // console.log(`[PracticeTest][${componentIdRef.current}] Clearing previous session storage before starting new exam`);
        examSessionStorage.clearExamData();
        
          // Prepare request data according to the backend StartExamRequest schema
        const requestData = {
          // Required fields
          examTypeId: currentSetup.examTypeId || (currentSetup.examType && currentSetup.examType !== 'ALL' ? currentSetup.examType : null),
          type: "Practice",
          title: currentSetup.title.trim(), // Note: Capital T as per backend API
          limit: currentSetup.questionCount ? parseInt(currentSetup.questionCount) : 50
        };        // Add optional fields only when they have values
        if (currentSetup.system && currentSetup.system.length > 0 && !currentSetup.system.includes('ALL')) {
          // Extract system IDs from the system names using systemOptions
          const systemIds = currentSetup.system.map(systemName => {
            const systemCategory = props.systemOptions?.find(s => s.name === systemName);
            // console.log(`[PracticeTest] Mapping system '${systemName}' to category:`, systemCategory);
            // Convert to number to ensure backend receives numeric IDs
            return systemCategory?.id ? parseInt(systemCategory.id, 10) : null;
          }).filter(id => id !== null && !isNaN(id));
          
          // console.log(`[PracticeTest] System IDs for API request (converted to numbers):`, systemIds);
          
          if (systemIds.length > 0) {
            // Ensure categoryIds is always an array
            requestData.categoryIds = Array.isArray(systemIds) ? systemIds : [systemIds];
          }
        }

        if (currentSetup.discipline && currentSetup.discipline.length > 0 && !currentSetup.discipline.includes('ALL')) {
          // Extract discipline IDs from the discipline names using disciplineOptions
          const disciplineIds = currentSetup.discipline.map(disciplineName => {
            const disciplineCategory = props.disciplineOptions?.find(d => d.name === disciplineName);
            // console.log(`[PracticeTest] Mapping discipline '${disciplineName}' to category:`, disciplineCategory);
            // Convert to number to ensure backend receives numeric IDs
            return disciplineCategory?.id ? parseInt(disciplineCategory.id, 10) : null;
          }).filter(id => id !== null && !isNaN(id));
          
          // console.log(`[PracticeTest] Discipline IDs for API request (converted to numbers):`, disciplineIds);
          
          if (disciplineIds.length > 0) {
            // Ensure we're always working with arrays and merging properly
            const existingCategoryIds = Array.isArray(requestData.categoryIds) ? requestData.categoryIds : [];
            const newDisciplineIds = Array.isArray(disciplineIds) ? disciplineIds : [disciplineIds];
            requestData.categoryIds = [...existingCategoryIds, ...newDisciplineIds];
          }
        }

        // console.log(`[PracticeTest] Final categoryIds for API request:`, requestData.categoryIds);
        // console.log(`[PracticeTest] Final categoryIds data types:`, requestData.categoryIds?.map(id => ({ value: id, type: typeof id })));
        // console.log(`[PracticeTest] Complete request data:`, JSON.stringify(requestData, null, 2));

        if (currentSetup.difficulty && currentSetup.difficulty.length > 0 && !currentSetup.difficulty.includes('ALL')) {
          requestData.difficulty = currentSetup.difficulty;
        }

        // Add question filter booleans only when a specific filter is selected
        if (currentSetup.filterOption === "Questions I have never seen") {
          requestData.neverSeen = true;
        } else if (currentSetup.filterOption === "Questions I have answered incorrectly") {
          requestData.answeredIncorrectly = true;
        } else if (currentSetup.filterOption === "Questions I have answered correctly") {
          requestData.answeredCorrectly = true;
        } else if (currentSetup.filterOption === "Questions I have seen before") {
          requestData.seenBefore = true;
        }
        
        // Always add userFolderIds array to requestData
        requestData.userFolderIds = (currentSetup.userFolderIds && currentSetup.userFolderIds.length > 0) 
          ? currentSetup.userFolderIds 
          : [];
        
        // console.log(`[PracticeTest][${componentIdRef.current}] Starting new session with data:`, requestData);

        try {
          // Start a new exam session
          const callId = trackApiCall('/api/exam/start', 'POST');
          const response = await examApi.startExamSession(requestData);
          // console.log(`[PracticeTest][${componentIdRef.current}][API][${callId}] Received response:`, response);
            if (response && response.id) {
            // 🎯 FIX: Clear ALL exam state immediately when starting new exam
            // console.log(`[PracticeTest][${componentIdRef.current}] NEW EXAM: Clearing all previous exam state`);
            setExamResponses({}); // Clear centralized response map
            setSelectedAnswer(null);
            setIsAnswerSubmitted(false);
            setIsCorrect(false);
            setIsFlagged(false);
            setCurrentResponse(null);
            setCurrentQuestion(null);
            setCurrentAnswers([]);            setResponses([]);
            setQuestions([]);
            
            updateExamData(response);
            startTimeRef.current = Date.now();
            
            // Declare variables to store questions and responses data
            let fetchedQuestions = [];
            let fetchedResponses = [];
              // Load questions and responses using HATEOAS links
            if (response._links && response._links.questions && response._links.questions.href) {
              const questionsCallId = trackApiCall(response._links.questions.href, 'GET');
              const fetchedRawQuestions = await examApi.followHateoasLink(response._links.questions.href);
                // Process questions to ensure HATEOAS links are properly set
              const processedQuestions = Array.isArray(fetchedRawQuestions)
                ? fetchedRawQuestions.map(q => ensureMinimalAnswersLinkOnQuestion(q, response.id))
                : []; // Handle cases where fetchedRawQuestions might not be an array
                // Sort questions by ID before setting them
              setQuestions(processedQuestions);
            }
            
            // Fetch responses via HATEOAS - for new exams this will be empty array
            if (response._links && response._links.responses && response._links.responses.href) {
              const responsesCallId = trackApiCall(response._links.responses.href, 'GET');
              fetchedResponses = await examApi.followHateoasLink(response._links.responses.href);
              setResponses(fetchedResponses);
              // console.log(`[PracticeTest][${componentIdRef.current}][API][${responsesCallId}] Loaded ${fetchedResponses.length} responses (should be 0 for new exam)`);
                  // Initialize centralized response state - will be empty for new exam
              // console.log(`[PracticeTest][${componentIdRef.current}] About to initialize examResponses with ${fetchedResponses.length} responses:`, 
              //   fetchedResponses.map(r => ({
              //     questionId: r.questionId,
              //     answerIds: r.answerIds || [],
              //     answerIdsLength: (r.answerIds || []).length,
              //     isSkipped: r.isSkipped
              //   }))
              // );
              initializeExamResponses(fetchedResponses);
            } else {
              // No responses link - ensure empty state
              // console.log(`[PracticeTest][${componentIdRef.current}] No responses link found, setting empty responses`);
              setResponses([]);
              setExamResponses({});
            }            
            // Setup is complete, now show the test interface
            if (props.setCurrentSessionId) {
              props.setCurrentSessionId(response.id);
            }            // Update the current question to the first question
            if (fetchedQuestions && fetchedQuestions.length > 0) {
              if (handleGoToQuestion) {
                handleGoToQuestion(0);
              }
              
              // Load the first question using the proper loadQuestion function
              // This will handle hydration from the (empty) response state correctly
              await loadQuestion(fetchedQuestions[0], fetchedResponses);
            }
            
            if (props.setSetupComplete) {
              props.setSetupComplete(true);
            } else {
              // Update local state for setup complete
              setLocalSetupComplete(true);
            }
          } else {
            throw new Error('Invalid response from API');
          }
        } catch (err) {
          // console.error(`[PracticeTest][${componentIdRef.current}] Error starting session:`, err);
          // console.log(`[PracticeTest][${componentIdRef.current}] Error status:`, err.status);
          // console.log(`[PracticeTest][${componentIdRef.current}] Error data:`, err.data);
          // console.log(`[PracticeTest][${componentIdRef.current}] Error response:`, err.response);
          // console.log(`[PracticeTest][${componentIdRef.current}] Error message:`, err.message);
          
          // Check if this is a 403 error with detail field (like quota exceeded)
          // The error might be wrapped in different ways depending on the API response
          let errorDetail = null;
          
          // Check direct error data
          if (err.data && err.data.detail) {
            errorDetail = err.data.detail;
            // console.log(`[PracticeTest][${componentIdRef.current}] Found detail in err.data:`, errorDetail);
          }
          // Check if error data has status 403 with detail
          else if (err.data && err.data.status === 403 && err.data.detail) {
            errorDetail = err.data.detail;
            // console.log(`[PracticeTest][${componentIdRef.current}] Found detail in err.data with status 403:`, errorDetail);
          }
          // Check response data
          else if (err.response && err.response.data && err.response.data.detail) {
            errorDetail = err.response.data.detail;
            // console.log(`[PracticeTest][${componentIdRef.current}] Found detail in err.response.data:`, errorDetail);
          }
          // Check if response data has status 403 with detail
          else if (err.response && err.response.data && err.response.data.status === 403 && err.response.data.detail) {
            errorDetail = err.response.data.detail;
            // console.log(`[PracticeTest][${componentIdRef.current}] Found detail in err.response.data with status 403:`, errorDetail);
          }
          
          if (errorDetail) {
            setError(errorDetail);
          } else {
            setError('Failed to start the session. Please try again.');
          }
        }
      }
    } catch (err) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error initializing exam:`, err);
      setError(`Error initializing exam: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, sessionSetup, localSessionSetup, props]);

  // Check plan limits and usage metrics
  const checkPlanLimits = useCallback(async () => {
    try {
      setIsCheckingPlanLimits(true);
      // console.log(`[PracticeTest][${componentIdRef.current}] Checking plan limits...`);

      // Fetch metrics from API
      const metricsResponse = await apiService.get('/api/metrics');
      // console.log(`[PracticeTest][${componentIdRef.current}] Metrics response:`, metricsResponse);
      // console.log(`[PracticeTest][${componentIdRef.current}] Metrics response type:`, typeof metricsResponse);
      // console.log(`[PracticeTest][${componentIdRef.current}] Is array:`, Array.isArray(metricsResponse));

      // Handle both single object and array responses
      let questionMetric = null;
      if (Array.isArray(metricsResponse)) {
        questionMetric = metricsResponse.find(metric => metric.type === 'Question');
      } else if (metricsResponse && metricsResponse.type === 'Question') {
        questionMetric = metricsResponse;
      }

      if (!questionMetric) {
        // console.log(`[PracticeTest][${componentIdRef.current}] No Question metric found`);
        return;
      }

      // console.log(`[PracticeTest][${componentIdRef.current}] Found Question metric:`, questionMetric);

      // Check if limits are exceeded
      const dailyLimitExceeded = questionMetric.dailyLimit > 0 && questionMetric.dailyUsage >= questionMetric.dailyLimit;
      const monthlyLimitExceeded = questionMetric.monthlyLimit > 0 && questionMetric.monthlyUsage >= questionMetric.monthlyLimit;

      // console.log(`[PracticeTest][${componentIdRef.current}] Limit checks:`, {
      //   dailyLimit: questionMetric.dailyLimit,
      //   dailyUsage: questionMetric.dailyUsage,
      //   monthlyLimit: questionMetric.monthlyLimit,
      //   monthlyUsage: questionMetric.monthlyUsage,
      //   dailyLimitExceeded,
      //   monthlyLimitExceeded
      // });

      if (dailyLimitExceeded || monthlyLimitExceeded) {
        // console.log(`[PracticeTest][${componentIdRef.current}] Plan limit exceeded:`, {
        //   dailyLimitExceeded,
        //   monthlyLimitExceeded,
        //   dailyUsage: questionMetric.dailyUsage,
        //   dailyLimit: questionMetric.dailyLimit,
        //   monthlyUsage: questionMetric.monthlyUsage,
        //   monthlyLimit: questionMetric.monthlyLimit
        // });

        const limitData = {
          planName: questionMetric.planName,
          dailyUsage: questionMetric.dailyUsage,
          dailyLimit: questionMetric.dailyLimit,
          monthlyUsage: questionMetric.monthlyUsage,
          monthlyLimit: questionMetric.monthlyLimit,
          limitType: dailyLimitExceeded ? 'daily' : 'monthly'
        };

        // console.log(`[PracticeTest][${componentIdRef.current}] Setting plan limit data:`, limitData);
        setPlanLimitData(limitData);
        // console.log(`[PracticeTest][${componentIdRef.current}] Setting showPlanLimitBanner to true`);
        setShowPlanLimitBanner(true);
        
        // console.log(`[PracticeTest][${componentIdRef.current}] Banner should now show`);
        // console.log(`[PracticeTest][${componentIdRef.current}] Debug setup state:`, {
        //   setupComplete,
        //   localSetupComplete,
        //   showPlanLimitBanner: true,
        //   planLimitData: limitData
        // });
      } else {
        // console.log(`[PracticeTest][${componentIdRef.current}] Plan limits not exceeded`);
        setShowPlanLimitBanner(false);
      }
    } catch (error) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error checking plan limits:`, error);
      // Don't show error to user, just silently fail
    } finally {
      setIsCheckingPlanLimits(false);
    }
  }, []);

  // Pagination handlers
  const prevSidebarPage = () => {
    setSidebarPage(prev => Math.max(0, prev - 1));
  };
  
  const nextSidebarPage = () => {
    setSidebarPage(prev => Math.min(totalSidebarPages - 1, prev + 1));
  };
    
  // Dropdowns toggle handlers
  const toggleSystemDropdown = () => setIsSystemDropdownOpen(!isSystemDropdownOpen);
  const toggleDisciplineDropdown = () => setIsDisciplineDropdownOpen(!isDisciplineDropdownOpen);
  const toggleFilterDropdown = () => setIsFilterDropdownOpen(!isFilterDropdownOpen);
  const toggleExamTypeDropdown = () => setIsExamTypeDropdownOpen(!isExamTypeDropdownOpen);
  const toggleDifficultyDropdown = () => setIsDifficultyDropdownOpen(!isDifficultyDropdownOpen);
  const toggleUserFolderDropdown = () => setIsUserFolderDropdownOpen(!isUserFolderDropdownOpen);
    
  // Refs to track timers and component state
  const startTimeRef = useRef(Date.now());
  const timerIntervalRef = useRef(null);

  // Resume exam session from storage if available
  useEffect(() => {
    // console.log(`[PracticeTest][${componentIdRef.current}] 🔄 RESUME EFFECT TRIGGERED: setupComplete=${setupComplete}, isResume=${isResume}, urlExamId=${urlExamId}`);
    let isMounted = true;
    const resumeSessionFromStorage = async () => {
      if (setupComplete || !isMounted) return;
      try {
        // Check if this is a resume request
        if (isResume && urlExamId) {
          // console.log(`[PracticeTest][${componentIdRef.current}] 🔄 RESUME REQUEST: examId=${urlExamId}, URL=${window.location.href}`);
          
          // First try to validate the session from the backend
          try {
            // console.log(`[PracticeTest][${componentIdRef.current}] 📡 Validating session with backend...`);
            const sessionData = await examApi.getExamSessionById(urlExamId);
            if (sessionData) {
              // console.log(`[PracticeTest][${componentIdRef.current}] ✅ Backend session validated`);
              updateExamData(sessionData);
              
              // console.log(`[PracticeTest][${componentIdRef.current}] 📚 Fetching questions from backend...`);
              const questionsData = await examApi.getSessionQuestions(urlExamId);
              if (questionsData && questionsData.length > 0) {
                // console.log(`[PracticeTest][${componentIdRef.current}] 📋 Loaded ${questionsData.length} questions from backend`);
                const processedQuestions = questionsData.map(q => ensureMinimalAnswersLinkOnQuestion(q, urlExamId));
                setQuestions(processedQuestions);
                
                // Use stored responses or empty array
                const storedResponses = examSessionStorage.getResponses() || [];
                // console.log(`[PracticeTest][${componentIdRef.current}] 💾 Loaded ${storedResponses.length} stored responses`);
                setResponses(storedResponses);
                initializeExamResponses(storedResponses);
                
                // Set current question index (start from 0 or last position)
                const lastIndex = examSessionStorage.getCurrentQuestionIndex(urlExamId) || 0;
                // console.log(`[PracticeTest][${componentIdRef.current}] Retrieved saved question index: ${lastIndex} for exam ${urlExamId}`);
                const validIndex = Math.min(lastIndex, processedQuestions.length - 1);
                // console.log(`[PracticeTest][${componentIdRef.current}] 🎯 Resuming at question index: ${validIndex}`);
                if (handleGoToQuestion) {
                  handleGoToQuestion(validIndex);
                }
                
                // Load the current question
                // console.log(`[PracticeTest][${componentIdRef.current}] ⏳ Loading current question...`);
                await loadQuestion(processedQuestions[validIndex], storedResponses, true);
                
                // console.log(`[PracticeTest][${componentIdRef.current}] 💾 Saving session data to storage`);
                examSessionStorage.saveExamSession(sessionData, sessionData.id);
                examSessionStorage.saveQuestions(processedQuestions);
                examSessionStorage.saveResponses(storedResponses);
                examSessionStorage.saveCurrentQuestionIndex(validIndex, examData?.id || urlExamId);
                
                setIsLoading(false);
                // console.log(`[PracticeTest][${componentIdRef.current}] ✅ Resume completed successfully`);
                return;
              }
            }
          } catch (apiError) {
            console.warn(`[PracticeTest][${componentIdRef.current}] ⚠️ Backend validation failed:`, apiError.message);
          }
        }
        
        // Fallback to sessionStorage if backend validation fails or not resuming
        const storedExamData = examSessionStorage.getExamData(urlExamId);
        const storedQuestions = examSessionStorage.getQuestions();
        const storedResponses = examSessionStorage.getResponses();
        const storedCurrentIndex = examSessionStorage.getCurrentQuestionIndex(storedExamData?.id);
        
        // console.log(`[PracticeTest][${componentIdRef.current}] 🔄 FALLBACK RESUME:`, {
        //   hasExamData: !!storedExamData,
        //   questionsCount: storedQuestions?.length || 0,
        //   responsesCount: storedResponses?.length || 0,
        //   currentIndex: storedCurrentIndex
        // });
        
        if (!storedExamData || !storedQuestions || storedQuestions.length === 0) {
          // console.log(`[PracticeTest][${componentIdRef.current}] ❌ No stored session data found`);
          return;
        }
        
        setIsLoading(true);
          // Restore exam data and questions
        updateExamData(storedExamData);          // Process questions to ensure HATEOAS links are properly set
        const processedQuestions = Array.isArray(storedQuestions)
          ? storedQuestions.map(q => ensureMinimalAnswersLinkOnQuestion(q, storedExamData?.id))
          : []; // Handle cases where storedQuestions might not be an array
        
        setQuestions(processedQuestions);
          // Restore responses
        const processedResponses = Array.isArray(storedResponses) ? storedResponses : [];
        setResponses(processedResponses);
        
        // Initialize centralized response state from stored responses
        initializeExamResponses(processedResponses);
          // Restore current question index
        const validIndex = Math.min(storedCurrentIndex, processedQuestions.length - 1);
        if (handleGoToQuestion) {
          handleGoToQuestion(validIndex);
        }
        
        // Load the current question data
        if (processedQuestions.length > 0) {
          await loadQuestion(processedQuestions[validIndex], processedResponses, true);
        }
        
        setIsLoading(false);
      } catch (err) {
        // console.error(`[PracticeTest][${componentIdRef.current}] Error resuming exam session:`, err);
        setError(`Error resuming exam session: ${err.message}`);
        setIsLoading(false);
      }
    };
      resumeSessionFromStorage();
    return () => {
      isMounted = false;
    };
  }, [setupComplete, isResume, urlExamId]);

  // Cleanup effect to save session data when navigating away
  useEffect(() => {
    const handleBeforeUnload = () => {
      // console.log(`[PracticeTest][${componentIdRef.current}] 🚪 PAGE UNLOADING - saving session data`);
      if (examData && questions.length > 0) {
        examSessionStorage.saveExamSession(examData, examData?.id);
        examSessionStorage.saveQuestions(questions);
        examSessionStorage.saveResponses(responses);
        examSessionStorage.saveCurrentQuestionIndex(currentQuestionIndex, examData?.id || urlExamId);
        // console.log(`[PracticeTest][${componentIdRef.current}] 💾 Session data saved before unload`);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && examData && questions.length > 0) {
        // console.log(`[PracticeTest][${componentIdRef.current}] 👁️ TAB HIDDEN - saving session data`);
        examSessionStorage.saveExamSession(examData, examData?.id);
        examSessionStorage.saveQuestions(questions);
        examSessionStorage.saveResponses(responses);
        examSessionStorage.saveCurrentQuestionIndex(currentQuestionIndex, examData?.id || urlExamId);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [examData, questions, responses, currentQuestionIndex]);

  // Check plan limits on component mount
  useEffect(() => {
    // console.log(`[PracticeTest][${componentIdRef.current}] useEffect triggered - calling checkPlanLimits`);
    checkPlanLimits();
  }, [checkPlanLimits]);

  // Navigation blocking function to check if there's an active practice test
  const shouldBlockNavigation = useCallback(() => {
    // Check if there's an active practice session
    const hasActiveSession = examData && 
                             examData.status && 
                             examData.status !== 'Finished' && 
                             (setupComplete || localSetupComplete);
    
    // console.log(`[PracticeTest][${componentIdRef.current}] Navigation block check:`, {
    //   hasExamData: !!examData,
    //   examStatus: examData?.status,
    //   setupComplete,
    //   localSetupComplete,
    //   shouldBlock: hasActiveSession
    // });
    
    return hasActiveSession;
  }, [examData, setupComplete, localSetupComplete]);

  // Custom navigation blocking for React Router (alternative to useBlocker)
  useEffect(() => {
    const handlePopState = (event) => {
      if (shouldBlockNavigation()) {
        const confirmed = window.confirm("Are you sure? You have an active practice test going on and this will close the page. Your progress will be saved, but you'll need to resume the session later.");
        if (!confirmed) {
          // Push the current state back to prevent navigation
          window.history.pushState(null, '', window.location.pathname + window.location.search);
        }
      }
    };

    // Listen for browser back/forward button navigation
    window.addEventListener('popstate', handlePopState);
    
    // Push initial state to enable navigation blocking
    if (shouldBlockNavigation()) {
      window.history.pushState(null, '', window.location.pathname + window.location.search);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [shouldBlockNavigation]);

  // Sync examData from props when it changes
  useEffect(() => {
    if (propsExamData !== undefined && propsExamData !== examData) {
      setExamData(propsExamData);
    }
  }, [propsExamData, examData]);

  // Clear state when exam ID changes to prevent stale data
  useEffect(() => {
    if (examData?.id) {
      // console.log(`[PracticeTest][${componentIdRef.current}] Exam ID changed to: ${examData.id}, ensuring clean state`);
      // This effect ensures that when switching between exams, we don't have stale response data
      // The actual clearing happens in handleStartSession, but this provides extra safety
    }
  }, [examData?.id]);

  // Update URL when examId changes (programmatic navigation for new exam sessions)
  useEffect(() => {
    if (examData?.id && examData.id !== urlExamId) {
      const currentPath = window.location.pathname;
      const newPath = `/practice-test/${examData.id}`;
      
      // Only navigate if we're not already on the correct path
      if (currentPath !== newPath) {
        // console.log(`[PracticeTest][${componentIdRef.current}] Updating URL from ${currentPath} to ${newPath}`);
        // Use replace: true to avoid creating multiple history entries when starting new sessions
        navigate(newPath + window.location.search, { replace: true });
      }
    }
  }, [examData?.id, urlExamId, navigate]);

  // Fetch user folders on mount or use props if available
  useEffect(() => {
    // console.log('[PracticeTest] 🔍 Checking userFolders props:', {
    //   propsUserFolders: props.userFolders,
    //   propsUserFoldersLength: props.userFolders?.length,
    //   propsIsLoadingFolders: props.isLoadingFolders,
    //   currentUserFoldersLength: userFolders?.length
    // });

    // Use props if available, otherwise fetch
    if (props.userFolders !== undefined) {
      // console.log('[PracticeTest] ✅ Using userFolders from props:', props.userFolders?.length || 0, 'folders');
      setUserFolders(props.userFolders);
      setIsLoadingFolders(props.isLoadingFolders || false);
      // console.log('[PracticeTest] 📝 Set userFolders from props, isLoadingFolders:', props.isLoadingFolders || false);
      return;
    }

    // console.log('[PracticeTest] 🚀 Props not available, fetching user folders from API...');
    const fetchFolders = async () => {
      setIsLoadingFolders(true);
      setFolderError(null);
      try {
        const folders = await userFolderService.getAllFolders();
        // console.log('[PracticeTest] 📡 API returned folders:', folders?.length || 0, 'folders');
        setUserFolders(folders || []);
      } catch (err) {
        // console.error(`[PracticeTest][${componentIdRef.current}] ❌ Error fetching user folders:`, err);
        setFolderError('Failed to load folders.');
        setUserFolders([]);
      } finally {
        setIsLoadingFolders(false);
        // console.log('[PracticeTest] 🏁 User folder fetch complete, isLoadingFolders:', false);
      }
    };
    fetchFolders();
  }, [props.userFolders, props.isLoadingFolders]); // Run when props change

  // Effect to update which folders the current question is in
  useEffect(() => {
    const updateCurrentQuestionFolderStatus = async () => {
      if (!currentQuestion || !currentQuestion.id || !userFolders.length) {
        setCurrentQuestionFolderIds([]);
        return;
      }
      // To avoid spamming API on every minor change, let's add a small debounce or condition
      // For now, direct fetch:
      // console.log(`[PracticeTest][${componentIdRef.current}] Checking folder status for question ${currentQuestion.id}`);
      
      // Show loading state for folder checks if needed, or manage silently
      // For simplicity, not adding a separate loading state here yet.

      const folderChecks = userFolders.map(folder =>
        {
          // Add a check to ensure folder and folder.id are valid before making the API call
          if (!folder || folder.id === undefined || folder.id === null) {
            console.warn(`[PracticeTest][${componentIdRef.current}] Skipping folder check due to invalid folder object:`, folder);
            return Promise.resolve(null); // Resolve with null to skip this folder
          }
          return userFolderService.getSpecificSavedQuestionInFolder(folder.id, currentQuestion.id)
          .then(() => folder.id) // Question is in this folder
          .catch(err => {
            // For other errors, log them but don't break the UI for all folders
            // console.error(`[PracticeTest][${componentIdRef.current}] Error checking folder ${folder.id} for question ${currentQuestion.id}:`, err);
            return null; 
          }); // Semicolon for the return statement is optional here, but fine.
        } // This closing brace for the map callback was missing.
      );
      try {
        const results = await Promise.all(folderChecks);
        const newFolderIds = results.filter(id => id !== null);
        // console.log(`[PracticeTest][${componentIdRef.current}] Question ${currentQuestion.id} is in folders:`, newFolderIds);
        setCurrentQuestionFolderIds(newFolderIds);
      } catch (error) {
        // This catch is for Promise.all itself, individual errors are handled above
        // console.error(`[PracticeTest][${componentIdRef.current}] Error during Promise.all for folder checks:`, error);
        // Optionally set a general error state for folder checks
      }
    };

    // Debounce or throttle this if it causes too many API calls
    // For now, direct call:
    updateCurrentQuestionFolderStatus();

  }, [currentQuestion, userFolders]);

  useEffect(() => {
    // Start timer when exam session begins (when examData and questions are loaded)
    // Don't start timer if exam is already finished
    if (examData && questions.length > 0 && (setupComplete || localSetupComplete) && !examTimerRunning && examData.status !== 'Finished') {
      // console.log(`[PracticeTest][${componentIdRef.current}] Starting exam timer for exam:`, {
      //   examId: examData.id,
      //   examDataTimeSpent: examData.timeSpent,
      //   examStatus: examData.status,
      //   examDataKeys: Object.keys(examData),
      //   timestamp: new Date().toISOString()
      // });
      
      // Calculate initial time spent if this is a resumed session
      const existingTimeSpent = examData.timeSpent || 0;
      // console.log(`[PracticeTest][${componentIdRef.current}] Calculated existingTimeSpent:`, existingTimeSpent);
      
      examTimerService.start(existingTimeSpent, handleExamTimerUpdate);
      setExamTimerRunning(true);
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Exam timer started with:`, {
      //   initialTime: existingTimeSpent,
      //   running: true
      // });   
     } else if (examData?.status === 'Finished') {
      // console.log(`[PracticeTest][${componentIdRef.current}] Exam is finished, initializing timer for review with total time spent:`, examData.timeSpent);
      
      // For finished exams, set the timer service to display the total time spent during review
      const totalTimeSpent = examData.timeSpent || 0;
      examTimerService.setTimeSpentForReview(totalTimeSpent);
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Timer initialized for finished exam with total time: ${totalTimeSpent}s`);
    }

    // Cleanup timer when component unmounts
    return () => {
      if (examTimerRunning) {
        // console.log(`[PracticeTest][${componentIdRef.current}] Stopping exam timer on cleanup`);
        examTimerService.stop();
        setExamTimerRunning(false);
      }
    };
  }, [examData, questions.length, setupComplete, localSetupComplete, examTimerRunning, handleExamTimerUpdate]);

  // Stop timer when exam status becomes "Finished"
  useEffect(() => {
    if (examData?.status === 'Finished' && examTimerRunning) {
      // console.log(`[PracticeTest][${componentIdRef.current}] Exam is finished, stopping timer`);
      examTimerService.stop();
      setExamTimerRunning(false);
    }
  }, [examData?.status, examTimerRunning]);

  // Update timer display every second - direct DOM update to avoid re-renders
  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime = examTimerService?.getCurrentTimeSpent ? examTimerService.getCurrentTimeSpent() : 0;
      
      // Update the DOM directly if element exists
      if (timerElementRef.current) {
        const hours = Math.floor(currentTime / 3600);
        const mins = Math.floor((currentTime % 3600) / 60);
        const secs = currentTime % 60;
        
        const formattedTime = hours > 0 
          ? `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
          : `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        
        timerElementRef.current.textContent = formattedTime;
      }
    }, 1000);

    // Initialize display with current service time
    const initialTime = examTimerService?.getCurrentTimeSpent ? examTimerService.getCurrentTimeSpent() : 0;
    timerDisplayRef.current = initialTime;
    
    // Update DOM initially
    if (timerElementRef.current) {
      const hours = Math.floor(initialTime / 3600);
      const mins = Math.floor((initialTime % 3600) / 60);
      const secs = initialTime % 60;
      
      const formattedTime = hours > 0 
        ? `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
        : `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      
      timerElementRef.current.textContent = formattedTime;
    }

    return () => clearInterval(interval);
  }, []);

  // Update timer reference when question changes
  useEffect(() => {
    if (!currentQuestion) return;

    // Clear any existing timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    // Reset timer reference to track start time for the new question
    startTimeRef.current = Date.now();

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [currentQuestion?.id]); // Only depend on question ID, not the entire question object



  // Browser navigation blocking - handle beforeunload event for tab/window close
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      const shouldBlock = shouldBlockNavigation();
      
      if (shouldBlock) {
        // console.log(`[PracticeTest][${componentIdRef.current}] Blocking browser navigation/close attempt`);
        
        // For modern browsers, the custom message is ignored but setting returnValue triggers the dialog
        const message = "Are you sure? You have an active practice test going on and this will close the page. Your progress will be lost.";
        event.returnValue = message;
        event.preventDefault();
        return message;
      }
    };

    // Add the event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlockNavigation]);

  // Clear note state when question changes
  useEffect(() => {
    // Clear auto-save timeout when question changes
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    setCurrentQuestionNote('');
    setNoteError(null);
    setHasUnsavedChanges(false);
    setNoteLastSaved(null);
    
    // Clear assets state when question changes
    setCurrentQuestionAssets([]);
    setAssetsError(null);
    
    // If notes are open, load the note for the new question
    if (showNotes && currentQuestion?.id) {
      loadQuestionNote(currentQuestion.id);
    }
    
    // Load assets for the new question
    if (currentQuestion?.id) {
      loadQuestionAssets(currentQuestion.id);
    }
  }, [currentQuestion?.id, showNotes]); // Depend on question ID and showNotes state

  // Cleanup auto-save timeout on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  // Save current question index to session storage whenever it changes
  useEffect(() => {
    // Only save if there's an active exam session and questions are loaded
    const hasActiveSession = examData && 
                             examData.status && 
                             examData.status !== 'Finished' && 
                             (setupComplete || localSetupComplete) &&
                             questions.length > 0;
    
    if (hasActiveSession && currentQuestionIndex >= 0) {
      // console.log(`[PracticeTest][${componentIdRef.current}] Saving current question index to session storage:`, currentQuestionIndex);
      examSessionStorage.saveCurrentQuestionIndex(currentQuestionIndex, examData?.id || urlExamId);
    }
  }, [currentQuestionIndex, examData, setupComplete, localSetupComplete, questions.length]);

  // Calculate time spent on-demand without causing re-renders
  const getCurrentTimeSpent = () => {
    return startTimeRef.current ? Date.now() - startTimeRef.current : 0;
  };    // Load a specific question and its answers
  const loadQuestion = async (question, allResponses = [], useDirectResponses = false) => {
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] ═══ LOAD QUESTION CALLED ═══`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 📋 Question ID: ${question?.id || question?.questionId || 'UNKNOWN'}`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 📊 All responses length: ${allResponses?.length || 0}`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 🔄 Use direct responses: ${useDirectResponses}`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 🕐 Load timestamp: ${new Date().toISOString()}`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 📍 Stack trace:`);
    // console.trace(`🚨 [PracticeTest][${componentIdRef.current}] loadQuestion origin`);
    
    if (!question) {
      console.error(`🚨 [PracticeTest][${componentIdRef.current}] ❌ Cannot load question: question is null or undefined`);
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Reset clues state FIRST, before any other operations
      // This ensures that when the component rerenders with the new question,
      // clues will be hidden regardless of any race conditions
      // console.log(`[PracticeTest][${componentIdRef.current}] Setting showClues to false - current state:`, showClues);
      
      // Force immediate state update to false - this is crucial
      setShowClues(false);
      
      // Check for clues in either bodyFormatted or body properties
      const hasCluesInBodyFormatted = question.bodyFormatted && 
        (question.bodyFormatted.includes('<clue>') || question.bodyFormatted.includes('['));
      const hasCluesInBody = question.body && 
        (question.body.includes('<clue>') || question.body.includes('['));
      
      const hasFormattedClues = hasCluesInBodyFormatted || hasCluesInBody;
        // Create formatted versions of the question content
      const baseContent = question.bodyFormatted || question.body || '';
      
      // Version WITHOUT clue highlighting - keep content but remove clue tags only
      const formattedWithoutClues = baseContent
        .replace(/<clue[^>]*>/gi, '') // Remove opening <clue> tags
        .replace(/<\/clue>/gi, '') // Remove closing </clue> tags
        .replace(/\s+/g, ' ') // Clean up extra whitespace
        .trim();      // Version WITH clue highlighting - add highlighting styles to clue content
      const formattedWithClues = baseContent
        .replace(/<clue([^>]*)>/gi, (match, attributes) => {
          return `<span class="clue-highlight"${attributes}>`;
        }) // Convert opening clue tags to styled spans with CSS class only
        .replace(/<\/clue>/gi, '</span>') // Convert closing </clue> tags to spans
        .replace(/\s+/g, ' ') // Clean up extra whitespace
        .trim();
      
      // Add the formatted versions to the question object
      const enhancedQuestion = {
        ...question,
        formattedWithClues,
        formattedWithoutClues,
        hasClues: hasFormattedClues // Add a flag to easily check if this question has clues
      };
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Enhanced question with formatted content:`, {
      //   questionId: enhancedQuestion.id,
      //   hasFormattedClues
      // });
        // console.log(`[PracticeTest][${componentIdRef.current}] Setting currentQuestion, reference ID:`, Object.prototype.toString.call(enhancedQuestion));
      
      // Ensure the question has examId for answer submission
      const questionWithExamId = ensureMinimalAnswersLinkOnQuestion(enhancedQuestion, examData?.id);
      
      // Store question in state
      setCurrentQuestion(questionWithExamId);
      
      // Ensure allResponses is an array even if it's undefined
      const safeResponses = Array.isArray(allResponses) ? allResponses : [];
      
      // Find existing response for this question
      const existingResponse = safeResponses.find(r => r.questionId === question.id);
      setCurrentResponse(existingResponse || null);
      
      // console.log(`[PracticeTest][${componentIdRef.current}] loadQuestion - existingResponse check:`, {
      //   questionId: question.id,
      //   hasExistingResponse: !!existingResponse,
      //   existingResponse: existingResponse,
      //   currentSelectedAnswer: selectedAnswer,
      //   currentIsAnswerSubmitted: isAnswerSubmitted,
      //   currentIsCorrect: isCorrect      });
      
      // Set flag state from centralized response (not from allResponses array)
      const centralizedResponse = getCurrentQuestionResponse(question.id);
      setIsFlagged(centralizedResponse?.isFlagged || false);
      
      // Reset answer state only if no centralized response exists
      if (!centralizedResponse) {
        // console.log(`[PracticeTest][${componentIdRef.current}] No centralized response - resetting UI state to blank`);
        setSelectedAnswer(null);
        setIsAnswerSubmitted(false);
        setIsCorrect(false);
      } else {
        // console.log(`[PracticeTest][${componentIdRef.current}] Found centralized response - will hydrate from it:`, centralizedResponse);
      }
      
      // Get answers from HATEOAS link if available
      const answersLink = question._links && getLinkHref(question._links, 'answers');
      let answersData = [];
      
      if (answersLink) {
        try {
          const callId = trackApiCall(answersLink, 'GET');
          // console.log(`[PracticeTest][${componentIdRef.current}][API][${callId}] FETCH START: Answers from HATEOAS link:`, answersLink);
          
          const startTime = Date.now();
          answersData = await examApi.followHateoasLink(answersLink);
          const duration = Date.now() - startTime;
          
          // console.log(`[PracticeTest][${componentIdRef.current}][API][${callId}] FETCH COMPLETE: Answers retrieved:`, answersData.length);
          
          // console.log(`[PracticeTest][${componentIdRef.current}] Setting currentAnswers, new length:`, answersData.length);
          
          // Hydrate UI state from centralized responses
          const responsesForHydration = useDirectResponses ? 
            allResponses.reduce((map, r) => { map[r.questionId] = r; return map; }, {}) : 
            null;
          hydrateCurrentQuestionState(question.id, answersData, responsesForHydration);
        } catch (answerError) {
          // console.error(`[PracticeTest][${componentIdRef.current}] Error loading answers:`, answerError);
          // Fall back to answers in the question object if available
          if (question.answers && Array.isArray(question.answers) && question.answers.length > 0) {
            setCurrentAnswers(question.answers);
          }
        }
      }
        // Reset timer for the new question
      startTimeRef.current = Date.now();
      
      setIsLoading(false);
    } catch (err) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error loading question:`, err);
      setError(`Error loading question: ${err.message}`);
      setIsLoading(false);
    }
  };

  // Enhanced helper function to close AI chat if open
  const closeAIChatIfOpen = useCallback(async () => {
    if (!aiChatRef.current) return false;
    
    try {
      const chatState = aiChatRef.current.getChatState?.() || {};
      // console.log(`[PracticeTest][${componentIdRef.current}] Checking AI chat state before navigation:`, chatState);
      
      if (chatState.isOpen || chatState.isMinimized || chatState.hasActiveConnection) {
        // console.log(`[PracticeTest][${componentIdRef.current}] AI chat is active, disconnecting before navigation`);
        
        // Call disconnect method to properly close websocket and UI
        if (typeof aiChatRef.current.disconnect === 'function') {
          aiChatRef.current.disconnect();
        } else if (typeof aiChatRef.current.closeChat === 'function') {
          aiChatRef.current.closeChat();
        }
        
        // Update local state
        setShowAIChat(false);
        setAiChatState({
          isOpen: false,
          isMinimized: false,
          isConnected: false
        });
        
        // Small delay to ensure cleanup completes
        await new Promise(resolve => setTimeout(resolve, 100));
        return true;
      }
      return false;
    } catch (error) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error closing AI chat:`, error);
      return false;
    }
  }, []);

  // Update AI chat state when chat opens/closes
  const handleAIChatOpen = useCallback(() => {
    // console.log(`[PracticeTest][${componentIdRef.current}] AI chat opened`);
    setShowAIChat(true);
    setAiChatState(prev => ({ ...prev, isOpen: true }));
  }, []);

  const handleAIChatClose = useCallback(() => {
    // console.log(`[PracticeTest][${componentIdRef.current}] AI chat closed`);
    setShowAIChat(false);
    setAiChatState({
      isOpen: false,
      isMinimized: false,
      isConnected: false
    });
  }, []);

  // Monitor AI chat state changes
  useEffect(() => {
    if (aiChatRef.current && showAIChat) {
      const checkChatState = () => {
        const chatState = aiChatRef.current?.getChatState?.() || {};
        setAiChatState(chatState);
      };
      
      // Check state periodically when chat is open
      const interval = setInterval(checkChatState, 1000);
      return () => clearInterval(interval);
    }
  }, [showAIChat]);

  // Cleanup AI chat on component unmount
  useEffect(() => {
    return () => {
      // Ensure AI chat is properly disconnected when component unmounts
      if (aiChatRef.current && typeof aiChatRef.current.disconnect === 'function') {
        // console.log(`[PracticeTest][${componentIdRef.current}] Component unmounting, disconnecting AI chat`);
        aiChatRef.current.disconnect();
      }
    };
  }, []);

  // Navigate to a question by index with AI chat handling
  const goToQuestion = async (index, skipSave = false) => {
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] ═══ NAVIGATION TO QUESTION ${index} STARTED ═══`);
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 🧭 Navigation details:`, {
    //   fromIndex: currentQuestionIndex,
    //   toIndex: index,
    //   questionsLength: questions.length,
    //   responsesLength: responses.length,
    //   aiChatState,
    //   skipSave,
    //   currentState: {
    //     selectedAnswer,
    //     isAnswerSubmitted,
    //     isCorrect,
    //     isFlagged
    //   },
    //   timestamp: new Date().toISOString()
    // });
    // console.log(`🚨 [PracticeTest][${componentIdRef.current}] 📍 Navigation stack:`);
    // console.trace(`🚨 [PracticeTest][${componentIdRef.current}] goToQuestion origin`);
    
    if (index < 0 || index >= questions.length || index === currentQuestionIndex) {
      // console.log(`🚨 [PracticeTest][${componentIdRef.current}] ❌ Navigation cancelled - invalid index or same question`);
      return;
    }

    // CRITICAL: Close AI chat before navigation if it's open
    const aiChatWasClosed = await closeAIChatIfOpen();
    if (aiChatWasClosed) {
      // console.log(`[PracticeTest][${componentIdRef.current}] AI chat was closed before navigation`);
    }

    // Only save current question state if it's not already answered/locked AND we're not skipping the save
    if (currentQuestion && !skipSave) {
      const isCurrentLocked = isCurrentQuestionLocked(currentQuestion.id);
      // console.log(`[PracticeTest][${componentIdRef.current}] Checking if current question needs saving:`, {
      //   questionId: currentQuestion.id,
      //   isLocked: isCurrentLocked,
      //   hasSelectedAnswer: !!selectedAnswer,
      //   isAnswerSubmitted,
      //   selectedAnswer
      // });
      
      if (!isCurrentLocked) {
        // console.log(`[PracticeTest][${componentIdRef.current}] Saving current question state before navigation (question not locked)...`);
        await saveCurrentQuestionState(true);
      } else {
        // console.log(`[PracticeTest][${componentIdRef.current}] Skipping save - question already answered/locked`);
      }
    } else if (skipSave) {
      // console.log(`[PracticeTest][${componentIdRef.current}] Skipping save - skipSave flag is true (just saved in handleSkipQuestion)`);
    }
    
    // Important: Reset clues state immediately before navigation
    // This prevents any race conditions where clues might be shown during load
    // console.log(`[PracticeTest][${componentIdRef.current}] Navigation: resetting showClues to false before loading question`);
    setShowClues(false);
    
    // console.log(`[PracticeTest][${componentIdRef.current}] Setting current question index to: ${index}`);
    // Use container's navigation handler to update the index
    if (handleGoToQuestion) {
      handleGoToQuestion(index);
    }
    // console.log(`[PracticeTest][${componentIdRef.current}] Target question debug:`, {
    //   questionId: questions[index]?.id,
    //   responsesCount: responses.length,
    //   responsesForTargetQuestion: responses.filter(r => r.questionId === questions[index]?.id).length
    // });

    await debouncedLoadQuestion(questions[index], responses);

    // console.log(`[PracticeTest][${componentIdRef.current}] ===== NAVIGATION TO QUESTION ${index} COMPLETED =====`);
  };
  
  // Go to next question
  const goToNextQuestion = async (skipSave = false) => {
    if (currentQuestionIndex >= questions.length - 1) return;
    await goToQuestion(currentQuestionIndex + 1, skipSave);
  };
  
  // Go to previous question
  const goToPrevQuestion = async (skipSave = false) => {
    await goToQuestion(currentQuestionIndex - 1, skipSave);
  };
  
  // Skip current question - save flag state and navigate to next without marking as answered
  const handleSkipQuestion = async () => {
    // console.log(`[PracticeTest][${componentIdRef.current}] ===== SKIP QUESTION STARTED =====`);
    // console.log(`[PracticeTest][${componentIdRef.current}] Skipping question ${currentQuestion?.id} at index ${currentQuestionIndex}`);
    
    // Save current question state (including flag state) to the API when skipping
    // This ensures flag state is preserved even for skipped questions
    // Check both local isFlagged state AND centralized response state
    const centralizedResponse = getCurrentQuestionResponse(currentQuestion?.id);
    const hasFlag = isFlagged || centralizedResponse?.isFlagged;
    
    if (hasFlag || centralizedResponse) {
      // console.log(`[PracticeTest][${componentIdRef.current}] Question is flagged or has existing response - saving state before skip`, {
      //   localIsFlagged: isFlagged,
      //   centralizedIsFlagged: centralizedResponse?.isFlagged,
      //   hasFlag,
      //   centralizedResponse
      // });
      await saveCurrentQuestionState(true); // skipOnly=true to save flag state without answers
    }
    
    // Don't mark question as answered/submitted - just navigate to next question
    // This allows the user to come back and answer it later
    if (currentQuestionIndex < questions.length - 1) {
      // Clear selected answer to prevent duplicate save in goToQuestion
      setSelectedAnswer(null);
      // Pass skipSave=true to prevent goToQuestion from saving again
      await goToNextQuestion(true);
    } else {
      // console.log(`[PracticeTest][${componentIdRef.current}] Already at last question, cannot skip`);
    }
    
    // console.log(`[PracticeTest][${componentIdRef.current}] ===== SKIP QUESTION COMPLETED =====`);
  };  // Save current question state to API
  const saveCurrentQuestionState = async (skipOnly = false) => {
    if (!currentQuestion || !currentResponse) return;
    
    // Check if exam has ended before allowing backend saves
    if (shouldPreventBackendUpdates(examData)) {
      // console.log(`[PracticeTest][${componentIdRef.current}] Exam has ended or is finished, skipping save operation`);
      return false;
    }

    try {
      // Calculate time spent on this question
      const elapsed = calculateTimeSpent(startTimeRef.current);
      
      // Get the most current flag state from centralized store OR local state
      const centralizedResponse = getCurrentQuestionResponse(currentQuestion?.id);
      const currentFlagState = centralizedResponse?.isFlagged ?? isFlagged;
      
      // Debug: Log current state before preparing payload
      // console.log(`[PracticeTest][${componentIdRef.current}] Debug saveCurrentQuestionState:`, {
      //   selectedAnswer,
      //   skipOnly,
      //   currentAnswersLength: currentAnswers?.length || 0,
      //   currentAnswersStructure: currentAnswers?.map(a => ({ id: a?.id, body: a?.body })) || [],
      //   localIsFlagged: isFlagged,
      //   centralizedIsFlagged: centralizedResponse?.isFlagged,
      //   currentFlagState,
      //   elapsed
      // });
      
      // Get the answer IDs using the utility function
      // If skipOnly is true, we still send the time spent but with empty answers array
      const answerData = prepareResponsePayload(
        elapsed,
        currentFlagState,  // Use the centralized flag state
        skipOnly ? null : selectedAnswer,  // Don't send answers if skipOnly=true
        currentAnswers
      );
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Prepared answer data:`, answerData);
      
      // Get the answer IDs for local state update
      let answerIds = [];
      if (!skipOnly && selectedAnswer && currentAnswers.length > 0) {
        const answerIndex = selectedAnswer.charCodeAt(0) - 65; // Convert A, B, C to 0, 1, 2
        if (answerIndex >= 0 && answerIndex < currentAnswers.length) {
          answerIds = [currentAnswers[answerIndex].id];
        }
      }
      
      // Use the save link from the response or construct the URL
      let saveLink;
      
      // First check if the response has a save link (HATEOAS)
      if (currentResponse && currentResponse._links && currentResponse._links.save && currentResponse._links.save.href) {
        // Prioritize the save link from HATEOAS
        saveLink = currentResponse._links.save.href;
        // console.log(`[PracticeTest][${componentIdRef.current}] Using HATEOAS save link: ${saveLink}`);
      } 
      // Fall back to constructing URL using examId and questionId from the response
      else if (currentResponse && currentResponse.examId && currentQuestion) {
        saveLink = `/api/exam/${currentResponse.examId}/question/${currentQuestion.id}`;
        // console.log(`[PracticeTest][${componentIdRef.current}] Constructed save link from IDs: ${saveLink}`);
      }
      // Fall back to self link if save link is not available
      else if (currentResponse && currentResponse._links && currentResponse._links.self && currentResponse._links.self.href) {
        saveLink = currentResponse._links.self.href;
        // console.log(`[PracticeTest][${componentIdRef.current}] Using self link as fallback: ${saveLink}`);
      } 
      // Last resort - construct URL manually with response ID
      else {
        saveLink = `/api/examquestionresponse/${currentResponse.id}`;
        // console.log(`[PracticeTest][${componentIdRef.current}] Using last resort manually constructed link: ${saveLink}`);
      }
        // Construct the response object for local state update
      const response = {
        ...currentResponse,
        timeSpent: elapsed,
        isFlagged: currentFlagState,  // Use the centralized flag state
        answerIds: answerIds
      };
      
      // Save to the API using POST method for save link
      const callId = trackApiCall(saveLink, 'POST');
      // // console.log(`[PracticeTest][${componentIdRef.current}][API][${callId}] Saving question state:`, {
      //   questionId: currentQuestion.id,
      //   responseId: currentResponse?.id,
      //   skipOnly,
      //   answerIds,
      //   timeSpent: elapsed,
      //   isFlagged
      // });
      
      await examApi.submitAnswer(saveLink, answerData);
      
      // Update local responses array
      setResponses(prevResponses => {
        const updatedResponses = [...prevResponses];
        
        // Find and replace the existing response or add a new one
        const existingIndex = updatedResponses.findIndex(r => r.questionId === currentQuestion.id);
        
        if (existingIndex >= 0) {
          updatedResponses[existingIndex] = response;
        } else {
          updatedResponses.push(response);
        }
        
        // Save to session storage
        examSessionStorage.saveResponses(updatedResponses);
        
        return updatedResponses;
      });
      
      setCurrentResponse(response);
      return true;
    } catch (err) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error saving question state:`, err);
      setError(`Error saving question state: ${err.message}`);
      return false;
    }
  };  // Submit answer to current question - DEPRECATED: Now handled by CleanAnswerOptions
  const submitAnswer = async () => {
    // console.log(`[PracticeTest][${componentIdRef.current}] submitAnswer called but should be handled by CleanAnswerOptions`);
    // This function is now deprecated - CleanAnswerOptions handles submission
    // and triggers the onAnswerSubmitted callback properly
  };
    // Skip current question - DEPRECATED: Now handled by CleanAnswerOptions 
  
 
  const skipQuestion = async () => {
    // console.log(`[PracticeTest][${componentIdRef.current}] skipQuestion called but should be handled by CleanAnswerOptions`);
    // This function is now deprecated - CleanAnswerOptions handles skipping
    // and triggers the appropriate callbacks
  };  // Toggle showing clues in the question text
  const toggleClues = () => {
    const newValue = !showClues;
    // console.log(`[PracticeTest][${componentIdRef.current}] Toggling clues from ${showClues} to ${newValue}, questionId: ${currentQuestion?.id}`);
    
    // Set the state with the new value
    setShowClues(newValue);
    
    // Log additional context for debugging
    setTimeout(() => {
      // console.log(`[PracticeTest][${componentIdRef.current}] After toggle - Current showClues state: ${newValue ? 'SHOWING CLUES' : 'HIDING CLUES'}`);
    }, 50);
  };  // Handle feedback submission
  const handleFeedbackSubmit = async (feedbackText) => {
    // console.log(`[PracticeTest][${componentIdRef.current}] Feedback submission started:`, feedbackText);
    
    try {
      // Prepare feedback payload for API
      const feedbackPayload = {
        feedbackText: feedbackText,
        questionId: currentQuestion?.id,
        examId: examData?.id,
        sessionId: examData?.sessionId,
        timestamp: new Date().toISOString()
      };

      // console.log(`[PracticeTest][${componentIdRef.current}] Submitting feedback to API:`, feedbackPayload);

      // Submit feedback via API
      await examApi.submitFeedback(feedbackPayload);
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Feedback submitted successfully`);
      
      // Optional: Show success message to user
      // Could add a toast notification here
      
    } catch (error) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error submitting feedback:`, error);
      // Could show error message to user here
      throw error; // Re-throw so FeedbackModal can handle the error
    }
  };
  
  // Handler for form input changes
  const handleSetupChange = (e) => {
    // console.log(`[PracticeTest][${componentIdRef.current}] handleSetupChange called with:`, e);
    const { name, value } = e.target;
    
    // If parent component provides a setSessionSetup function, use that
    if (props.setSessionSetup) {
      props.setSessionSetup(prevSetup => ({
        ...prevSetup,
        [name]: value
      }));
    } else {
      // Otherwise use local state
      setSessionSetup(prevSetup => ({
        ...prevSetup,
        [name]: value
      }));
    }
  };
  
  // Handler for exam type changes
  const handleExamTypeChange = (examType) => {
    // console.log(`[PracticeTest][${componentIdRef.current}] Exam type selected:`, examType);
    if (!examType) return;
    
    // Set both the examType name and ID
    handleSetupChange({
      target: {
        name: 'examType',
        value: examType.name
      }
    });
    
    handleSetupChange({
      target: {
        name: 'examTypeId',
        value: examType.id
      }
    });
  };

  // Folder Management Handlers
  const handleCreateFolder = async (folderData) => {
    try {
      // console.log('[PracticeTest][handleCreateFolder] called with:', folderData);
      let newFolder = await userFolderService.createFolder(folderData);
      // console.log('[PracticeTest][handleCreateFolder] userFolderService.createFolder result:', newFolder);
      // Check if the API returned an array and extract the first element if so
      if (Array.isArray(newFolder) && newFolder.length > 0) {
        // console.log(`[PracticeTest][${componentIdRef.current}] API returned array for new folder, taking first element:`, newFolder[0]);
        newFolder = newFolder[0];
        // console.log(`[PracticeTest][${componentIdRef.current}] Processed API response for create folder:`, newFolder);
      }

      // Ensure the processed newFolder has an ID before adding it to state
      if (newFolder && newFolder.id !== undefined && newFolder.id !== null && typeof newFolder.id === 'number') {
        setUserFolders(prevFolders => {
          // Check if a folder with this ID already exists (shouldn't happen on create, but defensive)
          if (prevFolders.some(f => f.id === newFolder.id)) {
            console.warn(`[PracticeTest][${componentIdRef.current}] Attempted to add a new folder with an existing ID (${newFolder.id}). This might indicate an API issue or duplicate creation. The existing folder will be updated.`);
            return prevFolders.map(f => f.id === newFolder.id ? newFolder : f);
          }
          return [...prevFolders, newFolder];
        });
        return newFolder; // Return the created folder on success
      } else {
        // console.error(`[PracticeTest][${componentIdRef.current}] API returned invalid folder object on creation:`, newFolder);
      }
    } catch (err) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error creating folder:`, err);
      setFolderError('Failed to create folder.');
    }
    // Note: isSubmitting state is handled in the FolderManagementModal component
  };

  const handleUpdateFolder = async (folderId, folderData) => {
    try {
      const updatedFolder = await userFolderService.updateFolder(folderId, folderData);
      setUserFolders(prev => prev.map(f => f.id === folderId ? updatedFolder : f));
    } catch (err) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error updating folder:`, err);
      setFolderError('Failed to update folder.');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await userFolderService.deleteFolder(folderId);
      setUserFolders(prev => prev.filter(f => f.id !== folderId));
      // Also update currentQuestionFolderIds if the deleted folder was in it
      setCurrentQuestionFolderIds(prevIds => prevIds.filter(id => id !== folderId));
    } catch (err) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error deleting folder:`, err);
      setFolderError('Failed to delete folder.');
    }
  };

  const handleAddQuestionToFolder = async (folderId, questionId) => {
    if (!questionId) return;
    try {
      await userFolderService.addQuestionToFolder(folderId, questionId);
      setCurrentQuestionFolderIds(prev => [...prev, folderId]);
    } catch (err) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error adding question to folder:`, err);
      // Handle specific errors, e.g., question already in folder (though API might handle this with a specific status)
      setFolderError('Failed to add question to folder.');
    }
  };

  const handleRemoveQuestionFromFolder = async (folderId, questionId) => {
    if (!questionId) return;
    try {
      await userFolderService.removeQuestionFromFolder(folderId, questionId);
      setCurrentQuestionFolderIds(prev => prev.filter(id => id !== folderId));
    } catch (err) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error removing question from folder:`, err);
      setFolderError('Failed to remove question from folder.');
    }
  };

  const handleToggleFolderModal = () => {
    // Fetch folders again when opening the modal to ensure fresh data,
    // or rely on the initial fetch and updates through actions.
    // For now, let's assume folders are reasonably up-to-date.
    setShowFolderModal(prev => !prev);
  };

  // Question Notes Handlers
  const handleToggleNotes = () => {
    setShowNotes(!showNotes);
    // Load note when opening if we haven't loaded it yet for this question
    if (!showNotes && currentQuestion?.id && !currentQuestionNote) {
      loadQuestionNote(currentQuestion.id);
    }
  };

  // Handle navigation pin/unpin
  const handlePinNavigation = useCallback(() => {
    setIsNavigationPinned(true);
  }, []);

  const handleUnpinNavigation = useCallback(() => {
    setIsNavigationPinned(false);
  }, []);

  // Expose pin function to window for sidebar access
  useEffect(() => {
    window.handleNavigationPin = handlePinNavigation;
    return () => {
      delete window.handleNavigationPin;
    };
  }, [handlePinNavigation]);

  const loadQuestionNote = async (questionId) => {
    if (!questionId) return;
    
    setIsLoadingNote(true);
    setNoteError(null);
    
    try {
      // console.log(`[PracticeTest][${componentIdRef.current}] Loading question note for questionId:`, questionId);
      const response = await apiService.get(`/api/question/${questionId}/note`);
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Raw API response:`, {
      //   response,
      //   responseType: typeof response,
      //   isString: typeof response === 'string',
      //   responseLength: response?.length || 0
      // });
      
      // The API now returns plain text directly, so we can use it as-is
      const noteText = typeof response === 'string' ? response : String(response || '');
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Final noteText to set:`, noteText);
      
      setCurrentQuestionNote(noteText);
      setHasUnsavedChanges(false);
      setNoteLastSaved(noteText ? new Date() : null);
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Successfully set currentQuestionNote to:`, noteText);
    } catch (error) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error loading question note:`, error);
      
      // Handle 404 as normal case (no note exists yet)
      if (error.status === 404) {
        // console.log(`[PracticeTest][${componentIdRef.current}] Note not found (404), setting empty note`);
        setCurrentQuestionNote('');
        setHasUnsavedChanges(false);
        setNoteLastSaved(null);
      } else {
        // console.error(`[PracticeTest][${componentIdRef.current}] Unexpected error loading note:`, error);
        setNoteError('Failed to load note.');
        setCurrentQuestionNote('');
      }
    } finally {
      setIsLoadingNote(false);
    }
  };

  const saveQuestionNote = async (questionId, noteText) => {
    if (!questionId) return;
    
    setIsSavingNote(true);
    setNoteError(null);
    
    try {
      await apiService.post(`/api/question/${questionId}/note`, { note: noteText });
      
      // Note saved successfully
      // console.log(`[PracticeTest][${componentIdRef.current}] Question note saved successfully`);
      
      // Optionally, you can show a success message to the user
      // For example, using a toast notification
    } catch (error) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error saving question note:`, error);
      setNoteError('Failed to save note.');
      throw error; // Re-throw so the component can handle it
    } finally {
      setIsSavingNote(false);
    }
  };

  // Load assets for a question
  const loadQuestionAssets = async (questionId) => {
    if (!questionId) return;
    
    setIsLoadingAssets(true);
    setAssetsError(null);
    
    try {
      // console.log(`[PracticeTest][${componentIdRef.current}] Loading assets for question:`, questionId);
      const assets = await apiService.get(`/api/question/${questionId}/assets`);
      
      // console.log(`[PracticeTest][${componentIdRef.current}] Loaded assets:`, {
      //   count: assets?.length || 0,
      //   assets
      // });
      
      // Set assets (empty array if none exist)
      setCurrentQuestionAssets(Array.isArray(assets) ? assets : []);
    } catch (error) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error loading assets:`, error);
      
      // Handle 404 as normal case (no assets exist)
      if (error.status === 404) {
        // console.log(`[PracticeTest][${componentIdRef.current}] No assets found (404), setting empty array`);
        setCurrentQuestionAssets([]);
      } else {
        // console.error(`[PracticeTest][${componentIdRef.current}] Unexpected error loading assets:`, error);
        setAssetsError('Failed to load assets.');
        setCurrentQuestionAssets([]);
      }
    } finally {
      setIsLoadingAssets(false);
    }
  };

  const handleNoteChange = (e) => {
    const newValue = e.target.value;
    setCurrentQuestionNote(newValue);
    setHasUnsavedChanges(true);
    setNoteError(null);
    
    // Clear existing auto-save timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Set new auto-save timeout (save after 2 seconds of no typing)
    autoSaveTimeoutRef.current = setTimeout(() => {
      if (currentQuestion?.id) {
        handleAutoSave(currentQuestion.id, newValue);
      }
    }, 2000);
  };

  const handleAutoSave = async (questionId, noteText) => {
    try {
      await saveQuestionNote(questionId, noteText);
      setHasUnsavedChanges(false);
      setNoteLastSaved(new Date());
    } catch (error) {
      // Error handling is already done in saveQuestionNote
      console.warn(`[PracticeTest][${componentIdRef.current}] Auto-save failed for question ${questionId}`);
    }
  };

  const handleNoteSave = async () => {
    if (!currentQuestion?.id) return;
    
    // Clear auto-save timeout since we're manually saving
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    try {
      await saveQuestionNote(currentQuestion.id, currentQuestionNote);
      setHasUnsavedChanges(false);
      setNoteLastSaved(new Date());
    } catch (error) {
      // Error is already handled in saveQuestionNote
    }
  };

  const handleNoteKeyDown = (e) => {
    // Save with Ctrl+S or Cmd+S
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      handleNoteSave();
    }
  };
    // Helper functions for displaying selected options text
  const getSelectedSystemsText = () => {
    const currentSetup = sessionSetup || localSessionSetup;
    if (!currentSetup.system || currentSetup.system.length === 0) return "Select System";
    if (currentSetup.system.includes("ALL")) return "All Systems";
    if (currentSetup.system.length === 1) return currentSetup.system[0];
    return `${currentSetup.system.length} Systems Selected`;
  };
  
  const getSelectedDisciplinesText = () => {
    const currentSetup = sessionSetup || localSessionSetup;
    if (!currentSetup.discipline || currentSetup.discipline.length === 0) return "Select Discipline";
    if (currentSetup.discipline.includes("ALL")) return "All Disciplines";
    if (currentSetup.discipline.length === 1) return currentSetup.discipline[0];
    return `${currentSetup.discipline.length} Disciplines Selected`;
  };
  
  const getSelectedDifficultiesText = () => {
    const currentSetup = sessionSetup || localSessionSetup;
    if (!currentSetup.difficulty || currentSetup.difficulty.length === 0) return "Select Difficulty";
    if (currentSetup.difficulty.includes("ALL")) return "All Difficulties";
    if (currentSetup.difficulty.length === 1) return currentSetup.difficulty[0];
    return `${currentSetup.difficulty.length} Difficulties Selected`;
  };
  
  // Helper functions to check if options are selected
  const isSystemSelected = (system) => {
    const currentSetup = sessionSetup || localSessionSetup;
    return currentSetup.system && currentSetup.system.includes(system);
  };
  
  const isDisciplineSelected = (discipline) => {
    const currentSetup = sessionSetup || localSessionSetup;
    return currentSetup.discipline && currentSetup.discipline.includes(discipline);
  };
  
  const isDifficultySelected = (difficulty) => {
    const currentSetup = sessionSetup || localSessionSetup;
    return currentSetup.difficulty && currentSetup.difficulty.includes(difficulty);
  };  // Toggle flag on current question
  const toggleFlag = async () => {
    if (currentQuestion && currentResponse) {
      // Check if exam has ended before allowing flag updates
      if (shouldPreventBackendUpdates(examData)) {
        // console.log(`[PracticeTest][${componentIdRef.current}] Exam has ended or is finished, skipping flag update`);
        return;
      }

      try {
        // Toggle flag
        const newFlaggedState = !isFlagged;
        setIsFlagged(newFlaggedState);
          // Update flag in the response object
        const response = {
          ...currentResponse,
          isFlagged: newFlaggedState
        };
        
        // Calculate time spent
        const elapsed = calculateTimeSpent(startTimeRef.current);
        
        // Get selected answers from the response if available
        let selectedAnswers = null;
        if (response.answerIds && response.answerIds.length > 0) {
          // Find the answer index in currentAnswers and convert to letter
          const answerIndex = currentAnswers.findIndex(a => a.id === response.answerIds[0]);
          if (answerIndex >= 0) {
            selectedAnswers = String.fromCharCode(65 + answerIndex);
          }
        }
        
        // Use utility function to prepare payload with correct format
        const answerData = prepareResponsePayload(
          elapsed,
          newFlaggedState,
          selectedAnswers,
          currentAnswers
        );
        
        // Use the save link from the response or construct the URL
        let saveLink;
        
        // First check if the response has a save link (HATEOAS)
        if (currentResponse && currentResponse._links && currentResponse._links.save && currentResponse._links.save.href) {
          // Prioritize the save link from HATEOAS
          saveLink = currentResponse._links.save.href;
          // console.log(`[PracticeTest][${componentIdRef.current}] Using HATEOAS save link: ${saveLink}`);
        } 
        // Fall back to constructing URL using examId and questionId from the response
        else if (currentResponse && currentResponse.examId && currentQuestion) {
          saveLink = `/api/exam/${currentResponse.examId}/question/${currentQuestion.id}`;
          // console.log(`[PracticeTest][${componentIdRef.current}] Constructed save link from IDs: ${saveLink}`);
        }
        // Fall back to self link if save link is not available
        else if (currentResponse && currentResponse._links && currentResponse._links.self && currentResponse._links.self.href) {
          saveLink = currentResponse._links.self.href;
          // console.log(`[PracticeTest][${componentIdRef.current}] Using self link as fallback: ${saveLink}`);
        } 
        // Last resort - construct URL manually with response ID
        else {
          saveLink = `/api/examquestionresponse/${response.id}`;
          // console.log(`[PracticeTest][${componentIdRef.current}] Using last resort manually constructed link: ${saveLink}`);
        }
        
        const callId = trackApiCall(saveLink, 'POST'); // Use POST instead of PUT
        // console.log(`[PracticeTest][${componentIdRef.current}][API][${callId}] Updating flag state:`, {
        //   questionId: currentQuestion.id,
        //   newFlaggedState
        // });
        
        await examApi.submitAnswer(saveLink, answerData); // Use submitAnswer instead of saveQuestionState
        
        // Update local responses array
        setResponses(prevResponses => {
          const updatedResponses = [...prevResponses];
          
          // // Find and replace the existing response or add a new one
          const existingIndex = updatedResponses.findIndex(r => r.questionId === currentQuestion.id);
          
          if (existingIndex >= 0) {
            updatedResponses[existingIndex] = response;
          } else {
            updatedResponses.push(response);
          }
          
          // Save to session storage
          examSessionStorage.saveResponses(updatedResponses);
          
          return updatedResponses;
        });
        
        setCurrentResponse(response);      } catch (error) {
        // console.error(`[PracticeTest][${componentIdRef.current}] Error updating flag:`, error);
      }
    }
  };

  // End the exam session
  // State for session end message
  const [sessionEndMessage, setSessionEndMessage] = useState("");

  const endExam = async () => {
    if (!examData || isSessionEnding) return;
    try {
      setIsSessionEnding(true);
      // Stop exam timer and force final update
      let finalTimeSpent = 0;
      if (examTimerRunning) {
        // console.log(`[PracticeTest][${componentIdRef.current}] Stopping exam timer and forcing final update`);
        examTimerService.forceUpdate();
        finalTimeSpent = examTimerService.stop();
        setExamTimerRunning(false);
      } else {
        // If timer wasn't running, get the current time spent
        finalTimeSpent = examTimerService.getCurrentTimeSpent();
      }
      // console.log(`[PracticeTest][${componentIdRef.current}] Final time spent for exam end: ${finalTimeSpent}s`);
      // Save current question state before ending
      if (currentQuestion) {
        const isLocked = isCurrentQuestionLocked(currentQuestion.id);
        if (!isLocked) {
          // console.log(`[PracticeTest][${componentIdRef.current}] Question ${currentQuestion.id} is not answered, saving its state before ending exam.`);
          await saveCurrentQuestionState(true); // Save time spent and flag, but no answer
        }
      }
      const endLink = getLinkHref(examData._links, 'end');
      if (endLink) {
        await examApi.endExam(endLink, finalTimeSpent);
      }
      // Clear session storage
      examSessionStorage.clearExamData();
      // Redirect to results page or show results
      if (props.onEndSession) {
        props.onEndSession();
      } else {
        // Show a message and navigate to dashboard
        setSessionEndMessage('Exam session ended. Thank you!');
        // console.log(`[PracticeTest][${componentIdRef.current}] ⏰ NAVIGATING TO DASHBOARD in 2 seconds`);
        setTimeout(() => {
          // console.log(`[PracticeTest][${componentIdRef.current}] 🧭 NAVIGATING TO DASHBOARD now`);
          navigate('/dashboard');
        }, 2000); // Show message for 2 seconds before redirect
      }
    } catch (err) {
      // console.error(`[PracticeTest][${componentIdRef.current}] Error ending exam:`, err);
      setError(`Error ending exam: ${err.message}`);
    } finally {
      setIsSessionEnding(false);
    }
  };

  // Handle end session confirmation
  const handleConfirmEndSession = () => {
    setShowEndSessionModal(false);
    endExam();
  };

  // Handle cancel end session
  const handleCancelEndSession = () => {
    setShowEndSessionModal(false);
  };
  
  // Get the number of completed questions
  const completedCount = countCompletedQuestions(responses);
  
  // Get theme styles from props or use defaults
  const styles = themeStyles || {
    content: {
      // Match Dashboard card style: translucent panel on dark gradient
      background: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : '#ffffff',
      text: isDarkMode ? '#ffffff' : '#333333',
      border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.10)' : '1px solid rgba(0, 0, 0, 0.1)',
      shadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 4px 16px rgba(0, 0, 0, 0.1)'
    },
    sidebar: {
      // Slightly translucent to sit on the page gradient like dashboard sections
      background: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#f1f5f9',
      text: isDarkMode ? '#e2e8f0' : '#334155'
    },
    text: {
      primary: isDarkMode ? '#ffffff' : '#334155',
      secondary: isDarkMode ? '#94a3b8' : '#64748b'
    },
    button: {
      primary: {
        background: isDarkMode ? '#3b82f6' : '#3b82f6',
        text: '#ffffff',
        hover: isDarkMode ? '#2563eb' : '#2563eb'
      },
      secondary: {
        background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
        text: isDarkMode ? '#e2e8f0' : '#334155',
        hover: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'
      }
    },
    error: {
      color: '#ef4444'
    }
  };

  // Determine if the current question is the last one
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  // Show session setup form if setup is not complete
  if (!(setupComplete || localSetupComplete)) {
    return (
      <div className="practice-test" style={{
        /* Background provided by CSS theme; avoid inline override */
        color: styles.text.primary,
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        boxSizing: 'border-box'
      }}>
        {/* Show plan limit banner */}
        {showPlanLimitBanner && planLimitData && (
          <div className="plan-limit-modal-overlay">
            <div className={`plan-limit-card${isDarkMode ? ' dark' : ' light'}`}> 
            {(() => {
              return null;
            })()}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <FontAwesomeIcon 
                icon={faTimes}
                style={{ fontSize: '24px', color: isDarkMode ? '#f87171' : '#dc2626', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.12))' }}
              />
              <h3 style={{ 
                margin: 0,
                color: isDarkMode ? '#fca5a5' : '#dc2626',
                fontSize: '22px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                textShadow: isDarkMode ? '0 1px 2px rgba(0,0,0,0.18)' : 'none'
              }}>
                Plan Limit Reached
              </h3>
            </div>
            
            <div style={{
              textAlign: 'center',
              maxWidth: '600px',
              marginTop: '8px'
            }}>
              <p style={{ 
                margin: '0 0 12px 0',
                color: isDarkMode ? '#e5e7eb' : '#334155',
                fontSize: '16px',
                lineHeight: '1.6',
                fontWeight: '500'
              }}>
                You've reached your {planLimitData.limitType} question limit on the <strong>{planLimitData.planName}</strong> plan.
              </p>
              
              <div style={{
                display: 'flex',
                gap: '20px',
                justifyContent: 'center',
                marginBottom: '16px',
                marginTop: '8px'
              }}>
                {planLimitData.dailyLimit > 0 && (
                  <div style={{
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: isDarkMode ? '#fca5a5' : '#dc2626',
                      letterSpacing: '0.5px'
                    }}>
                      {planLimitData.dailyUsage} / {planLimitData.dailyLimit}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: isDarkMode ? '#fca5a5' : '#dc2626',
                      opacity: 0.8
                    }}>
                      Daily Usage
                    </div>
                  </div>
                )}
                
                {planLimitData.monthlyLimit > 0 && (
                  <div style={{
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: styles.text.primary
                    }}>
                      {planLimitData.monthlyUsage} / {planLimitData.monthlyLimit}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: styles.text.secondary
                    }}>
                      Monthly Usage
                    </div>
                  </div>
                )}
              </div>
              
              <p style={{ 
                margin: '0 0 20px 0',
                color: isDarkMode ? '#f3f4f6' : '#64748b',
                fontSize: '15px',
                fontWeight: '400'
              }}>
                Upgrade your plan to continue practicing and access more questions.
              </p>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              marginTop: '8px'
            }}>
              <button
                onClick={() => { navigate('/billing'); }}
                className="plan-limit-btn upgrade"
              >
                Upgrade Plan
              </button>
              
              <button
                onClick={() => { setShowPlanLimitBanner(false); }}
                className="plan-limit-btn dismiss"
              >
                Dismiss
              </button>
            </div>
            </div>
          </div>
        )}

        <div className="practice-session-spacer">
          {/* Debug info for spacing issues */}
          {(() => {
            if (typeof window !== 'undefined') {
              setTimeout(() => {
                const spacer = document.querySelector('.practice-session-spacer');
                const container = document.querySelector('.scaled-practice-session-container');
                if (spacer && container) {
                  const spacerRect = spacer.getBoundingClientRect();
                  const containerRect = container.getBoundingClientRect();
                  const containerStyles = window.getComputedStyle(container);
                  
                  const spaceAbove = containerRect.top;
                  const spaceBelow = window.innerHeight - containerRect.bottom;
                  const isWellCentered = Math.abs(spaceAbove - spaceBelow) < 50; // Within 50px is good
                  
                  console.log('🎯 CENTERING RESULTS:', {
                    viewport: `${window.innerWidth}x${window.innerHeight}`,
                    spaceAbove: Math.round(spaceAbove),
                    spaceBelow: Math.round(spaceBelow),
                    difference: Math.round(Math.abs(spaceAbove - spaceBelow)),
                    isWellCentered,
                    containerHeight: Math.round(containerRect.height),
                    transform: containerStyles.transform,
                    marginTop: containerStyles.marginTop,
                    overflow: containerRect.bottom > window.innerHeight ? 'YES' : 'NO'
                  });
                  
                  if (!isWellCentered) {
                    console.log('⚠️ NOT WELL CENTERED - Adjustment needed:', {
                      targetMarginTop: `calc(50vh - ${Math.round(containerRect.height/2)}px)`,
                      currentMarginTop: containerStyles.marginTop,
                      recommendedFix: spaceAbove > spaceBelow ? 'Reduce margin-top' : 'Increase margin-top'
                    });
                  }
                }
              }, 500); // Increased delay to ensure styles are applied
            }
            return null;
          })()}
        </div>
        
        <div className="scaled-practice-session-container" style={{
          width: '100%', 
          maxWidth: '800px',
          margin: '0 auto',
          backgroundColor: styles.content.background,
          boxShadow: styles.content.shadow,
          borderRadius: '12px',
          padding: '30px',
          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease'
        }}>
          {/* {console.log('[PracticeTest] 📤 Rendering SessionSetupForm with examTypes:', props.examTypes?.length || 0, 'types, isLoadingExamTypes:', props.isLoadingExamTypes)} */}
          <SessionSetupForm
            sessionSetup={sessionSetup || localSessionSetup}
            handleSetupChange={handleSetupChange}
            handleStartSession={handleStartSession}
            isLoading={isLoading}
            error={error}
            examTypes={props.examTypes || []}
            systems={systems}
            disciplines={props.disciplines || []}
            difficulties={difficulties}
            filterOptions={filterOptions}
            userFolders={userFolders}
            isLoadingFolders={isLoadingFolders}
            systemOptions={props.systemOptions || []}
            disciplineOptions={props.disciplineOptions || []}
            isDarkMode={isDarkMode}
            themeStyles={styles}
            availableQuestionsCount={props.availableQuestionsCount}
            isLoadingQuestionCount={props.isLoadingQuestionCount}
            fetchQuestionCount={props.fetchQuestionCount}
            isLoadingExamTypes={props.isLoadingExamTypes || false}
            examTypesError={props.examTypesError || null}
            isSystemDropdownOpen={isSystemDropdownOpen}
            isDisciplineDropdownOpen={isDisciplineDropdownOpen}
            isFilterDropdownOpen={isFilterDropdownOpen}
            isExamTypeDropdownOpen={isExamTypeDropdownOpen}
            isDifficultyDropdownOpen={isDifficultyDropdownOpen}
            isUserFolderDropdownOpen={isUserFolderDropdownOpen}
            toggleSystemDropdown={toggleSystemDropdown}
            toggleDisciplineDropdown={toggleDisciplineDropdown}
            toggleFilterDropdown={toggleFilterDropdown}
            toggleExamTypeDropdown={toggleExamTypeDropdown}
            toggleDifficultyDropdown={toggleDifficultyDropdown}
            toggleUserFolderDropdown={toggleUserFolderDropdown}
            getSelectedSystemsText={getSelectedSystemsText}
            getSelectedDisciplinesText={getSelectedDisciplinesText}
            getSelectedDifficultiesText={getSelectedDifficultiesText}
            isSystemSelected={isSystemSelected}
            isDisciplineSelected={isDisciplineSelected}
            isDifficultySelected={isDifficultySelected}
            handleExamTypeChange={handleExamTypeChange}
            handleSystemChange={(system) => {
              // console.log(`[PracticeTest][${componentIdRef.current}] System selected:`, system);
              const currentSetup = sessionSetup || localSessionSetup;
              let newSelection = [];

              if (system === 'ALL') {
                newSelection = ['ALL'];
              } else {
                // Get the current system selection without 'ALL'
                const currentSelection = currentSetup.system.filter(s => s !== 'ALL');
                
                // Check if this system is already selected
                if (currentSelection.includes(system)) {
                  // Remove this system
                  newSelection = currentSelection.filter(s => s !== system);
                } else {
                  // Add this system
                  newSelection = [...currentSelection, system];
                }
                
                // If no specific systems are selected, default back to 'ALL'
                if (newSelection.length === 0) {
                  newSelection = ['ALL'];
                }
              }

              handleSetupChange({
                target: {
                  name: 'system',
                  value: newSelection
                }
              });
            }}            handleDisciplineChange={(discipline) => {
              // console.log(`[PracticeTest][${componentIdRef.current}] Discipline selected:`, discipline);
              const currentSetup = sessionSetup || localSessionSetup;
              let newSelection = [];

              if (discipline === 'ALL') {
                newSelection = ['ALL'];
              } else {
                // Get the current discipline selection without 'ALL'
                const currentSelection = currentSetup.discipline.filter(d => d !== 'ALL');
                
                // Check if this discipline is already selected
                if (currentSelection.includes(discipline)) {
                  // Remove this discipline
                  newSelection = currentSelection.filter(d => d !== discipline);
                } else {
                  // Add this discipline
                  newSelection = [...currentSelection, discipline];
                }
                
                // If no specific disciplines are selected, default back to 'ALL'
                if (newSelection.length === 0) {
                  newSelection = ['ALL'];
                }
              }

              handleSetupChange({
                target: {
                  name: 'discipline',
                  value: newSelection
                }
              });
            }}handleFilterChange={(filter) => {
              // console.log(`[PracticeTest][${componentIdRef.current}] Filter selected:`, filter);
              handleSetupChange({
                target: {
                  name: 'filterOption',
                  value: filter
                }
              });
            }}            handleDifficultyChange={(difficulty) => {
              // console.log(`[PracticeTest][${componentIdRef.current}] Difficulty selected:`, difficulty);
              const currentSetup = sessionSetup || localSessionSetup;
              let newSelection = [];

              if (difficulty === 'ALL') {
                newSelection = ['ALL'];
              } else {
                // Get the current difficulty selection without 'ALL'
                const currentSelection = currentSetup.difficulty.filter(d => d !== 'ALL');
                
                // Check if this difficulty is already selected
                if (currentSelection.includes(difficulty)) {
                  // Remove this difficulty
                  newSelection = currentSelection.filter(d => d !== difficulty);
                } else {
                  // Add this difficulty
                  newSelection = [...currentSelection, difficulty];
                }
                
                // If no specific difficulties are selected, default back to 'ALL'
                if (newSelection.length === 0) {
                  newSelection = ['ALL'];
                }
              }

              handleSetupChange({
                target: {
                  name: 'difficulty',
                  value: newSelection
                }
              });
            }}

          />
        </div>
      </div>
    );
  }
  
  return (
    <div className="practice-test" style={{
      /* Background provided by CSS theme; avoid inline override */
      color: styles.text.primary,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch', // Changed from 'center' to 'stretch' to prevent centering
      justifyContent: 'flex-start',
      minHeight: 'fit-content',
      height: 'auto',
      padding: '15px', // Restore consistent padding
      boxSizing: 'border-box',
      overflow: 'visible'
    }}>
      {/* Show session end message */}
      {sessionEndMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: styles.content.background,
            color: styles.text.primary,
            padding: '40px 60px',
            borderRadius: '12px',
            boxShadow: styles.content.shadow,
            fontSize: '1.5rem',
            fontWeight: 600,
            textAlign: 'center'
          }}>
            {sessionEndMessage}
          </div>
        </div>
      )}
      
      {/* Show plan limit checking indicator */}

      {/* Show plan limit checking indicator */}
      {isCheckingPlanLimits && (!setupComplete && !localSetupComplete) && (
        <div style={{
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto 20px auto',
          backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
          border: `1px solid ${isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
          borderRadius: '12px',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px'
        }}>
          <FontAwesomeIcon 
            icon={faSpinner} 
            spin 
            style={{ 
              fontSize: '16px',
              color: '#3b82f6'
            }} 
          />
          <span style={{
            color: styles.text.secondary,
            fontSize: '14px'
          }}>
            Checking plan limits...
          </span>
        </div>
      )}
      
      {/* Show plan limit checking indicator */}
      {isCheckingPlanLimits && (!setupComplete && !localSetupComplete) && (
        <div style={{
          width: '100%',
          maxWidth: '800px',
          margin: '0 auto 20px auto',
          backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
          border: `1px solid ${isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
          borderRadius: '12px',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px'
        }}>
          <FontAwesomeIcon 
            icon={faSpinner} 
            spin 
            style={{ 
              fontSize: '16px',
              color: '#3b82f6'
            }} 
          />
          <span style={{
            color: styles.text.secondary,
            fontSize: '14px'
          }}>
            Checking plan limits...
          </span>
        </div>
      )}
      
      {/* Show loading indicator */}
      {isLoading && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80vh',
          width: '100%'
        }}>
          <div className="spinner"></div>
          <div style={{ marginTop: '20px', fontSize: '18px' }}>Loading...</div>
        </div>
      )}
      
      {/* Show error message */}
      {!isLoading && error && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          width: '100%',
          maxWidth: '800px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{ 
            color: styles.error.color, 
            fontSize: '18px', 
            maxWidth: '600px',
            textAlign: 'center',
            marginBottom: '24px'
          }}>
            {error}
          </div>
          
          <button onClick={() => window.location.reload()} style={{
            padding: '10px 20px',
            borderRadius: '4px',
            backgroundColor: styles.button.primary.background,
            color: styles.button.primary.text,
            border: 'none',
            cursor: 'pointer',
            fontSize: '16px',
            transition: 'all 0.3s ease'
          }}>
            Reload
          </button>
        </div>
      )}
        {(setupComplete || localSetupComplete) && !isLoading && !error && questions.length > 0 && currentQuestion && (
        <div className="pt-wrapper" style={{ 
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Top Header Bar with Timer, Exam Info, Progress, and Close */}
          <div className="pt-header" style={{
            width: '100%',
            backgroundColor: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(249, 250, 251, 0.95)',
            borderRadius: '12px',
            padding: '16px 24px',
            marginBottom: '20px',
            boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 16px rgba(0, 0, 0, 0.08)',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            {/* Left Side: Timer and Status */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              minWidth: '240px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <FontAwesomeIcon 
                  icon={faClock} 
                  style={{ 
                    fontSize: '20px',
                    color: examData?.status === 'Finished'
                      ? (isDarkMode ? '#f59e0b' : '#d97706')
                      : examTimerRunning 
                        ? (isDarkMode ? '#10b981' : '#059669') 
                        : (isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)')
                  }} 
                />
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    fontFamily: 'monospace'
                  }}
                  ref={timerElementRef}>
                    {(() => {
                      const currentTime = examTimerService?.getCurrentTimeSpent ? examTimerService.getCurrentTimeSpent() : 0;
                      const hours = Math.floor(currentTime / 3600);
                      const mins = Math.floor((currentTime % 3600) / 60);
                      const secs = currentTime % 60;
                      
                      if (hours > 0) {
                        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                      }
                      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
                    })()}
                  </div>

                  <div style={{
                    fontSize: '12px',
                    color: examData?.status === 'Finished'
                      ? (isDarkMode ? '#f59e0b' : '#d97706')
                      : examTimerRunning 
                        ? (isDarkMode ? '#10b981' : '#059669')
                        : (isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'),
                    fontWeight: 500
                  }}>
                    {examData?.status === 'Finished' 
                      ? '◆ Finished' 
                      : examTimerRunning 
                        ? '● Running' 
                        : '○ Paused'
                    }
                  </div>
                </div>
              </div>
            </div>

            {/* Center: Exam Info */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minWidth: '300px',
              gap: '30px',
              flexWrap: 'wrap'
            }}>
              
              {/* Exam Title with Type - Format: "EXAMTYPE - Title" */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px'
              }}>
                {((sessionSetup?.examType || examData?.type) || (sessionSetup?.title || examData?.title)) && (
                  <div style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    textAlign: 'center'
                  }}>
                    {(() => {
                      const examType = sessionSetup?.examType || examData?.type;
                      const title = sessionSetup?.title || examData?.title;
                      
                      if (examType && title) {
                        return `${examType} - ${title}`;
                      } else if (examType) {
                        return examType;
                      } else if (title) {
                        return title;
                      }
                      return '';
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* Right Side: Pinned Navigation and Close Button */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              {/* Question Navigation Button - Always visible */}
              {window.innerWidth > 768 && (
                <button
                  className="question-navigation-button"
                  onClick={() => {
                    // Toggle sidebar visibility
                    handleToggleSidebar();
                  }}
                  aria-pressed={isSidebarVisible}
                  title={isSidebarVisible ? "Hide Question Navigation" : "Show Question Navigation"}
                  style={{
                    padding: '8px 12px',
                    fontSize: '13px',
                    fontWeight: '500',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <FontAwesomeIcon icon={faList} />
                  Questions
                </button>
              )}

              {/* Lab Value Button */}
              <button
                className="lab-values-button"
                onClick={() => {
                  // console.log('[LabValueToggle] Before toggle:', {
                  //   showLabValueSidebar,
                  //   isSidebarVisible,
                  //   questionContainerWidth,
                  //   windowWidth: window.innerWidth,
                  //   questionContainerRect: questionContainerRef.current?.getBoundingClientRect(),
                  //   questionContainerStyle: questionContainerRef.current ? {
                  //     left: questionContainerRef.current.style.left,
                  //     width: questionContainerRef.current.style.width,
                  //     position: questionContainerRef.current.style.position
                  //   } : null
                  // });
                  
                  // If opening Lab Values, close Question Navigation first
                  if (!showLabValueSidebar && isSidebarVisible) {
                    toggleSidebar();
                  }
                  
                  setShowLabValueSidebar(!showLabValueSidebar);
                  
                  // Log after state update
                  setTimeout(() => {
                    // console.log('[LabValueToggle] After toggle:', {
                    //   showLabValueSidebar: !showLabValueSidebar,
                    //   isSidebarVisible,
                    //   questionContainerRect: questionContainerRef.current?.getBoundingClientRect(),
                    //   questionContainerStyle: questionContainerRef.current ? {
                    //     left: questionContainerRef.current.style.left,
                    //     width: questionContainerRef.current.style.width,
                    //     position: questionContainerRef.current.style.position
                    //   } : null
                    // });
                  }, 100);
                }}
                aria-pressed={showLabValueSidebar}
                title="Show Lab Values Reference"
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap'
                }}
              >
                <FontAwesomeIcon icon={faFlask} />
                Lab Values
              </button>

              <button
                ref={endSessionBtnRef}
                className="end-session-button"
                onClick={() => setShowEndSessionModal(true)}
                disabled={isSessionEnding || examData?.status === 'Finished'}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  fontWeight: '500',
                  borderRadius: '6px',
                  cursor: (isSessionEnding || examData?.status === 'Finished') ? 'not-allowed' : 'pointer',
                  opacity: (isSessionEnding || examData?.status === 'Finished') ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
                {examData?.status === 'Finished' ? 'Finished' : (isSessionEnding ? 'Ending...' : 'End Session')}
              </button>
            </div>
          </div>

          <div className="pt-row" style={{
            display: 'flex',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'flex-start', // Keep left-aligned
            gap: showLabValueSidebar && window.innerWidth >= 1024 ? '20px' : '15px', // Slightly larger gap when lab values is present
            position: 'relative'
          }}>
            {/* console.log('[PTRow] Rendering with:', {
              showLabValueSidebar,
              isSidebarVisible,
              windowWidth: window.innerWidth,
              timestamp: new Date().toISOString()
            }) */}
            {/* Sidebar Container: Question Navigation only */}
            {(window.innerWidth <= 768 || isSidebarVisible) && (
              <div className="practice-test-sidebar-container pt-sidebar" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
                flexShrink: 0,
                marginLeft: 0,
                paddingLeft: 0,
                position: 'relative',
                width: window.innerWidth <= 768 ? '100%' : '280px',
                maxWidth: window.innerWidth <= 768 ? 'none' : '280px',
                minWidth: window.innerWidth <= 768 ? 'auto' : '260px',
                flex: window.innerWidth <= 768 ? '1 1 auto' : '0 0 280px'
              }}>
                
                {/* Question Navigation Sidebar */}
                {(window.innerWidth <= 768 || isSidebarVisible) && (
                  <QuestionNavigationSidebar 
                    questions={questions}
                    responses={Object.values(examResponses)}
                    currentQuestionId={currentQuestion?.id}
                    goToQuestion={goToQuestion}
                    isDarkMode={isDarkMode}
                    themeStyles={styles}
                    sidebarPage={sidebarPage}
                    questionsPerPage={QUESTIONS_PER_PAGE}
                    totalSidebarPages={totalSidebarPages}
                    onPrevPage={prevSidebarPage}
                    onNextPage={nextSidebarPage}
                    toggleSidebar={toggleSidebar}
                  />
                )}
              </div>
            )}

            {/* Lab Value Sidebar (desktop - as flex item to push question container) */}
            {showLabValueSidebar && window.innerWidth >= 1024 && (
              <div 
                className="practice-test-sidebar-container pt-sidebar lab-value-sidebar-container" 
                id="lab-value-sidebar-container"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0',
                  flexShrink: 0,
                  width: labValueSISColumn ? '450px' : '400px',
                  maxWidth: labValueSISColumn ? '450px' : '400px',
                  minWidth: labValueSISColumn ? '450px' : '400px',
                  height: '100%',
                  maxHeight: 'none',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  backgroundColor: styles.content.background,
                  border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
                  borderRadius: '12px',
                  boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 16px rgba(0, 0, 0, 0.08)',
                  padding: '0',
                  margin: '0',
                  transition: 'width 0.3s ease'
                }}>
                <LabValueSidebar
                  isDarkMode={isDarkMode}
                  themeStyles={styles}
                  toggleSidebar={() => setShowLabValueSidebar(false)}
                  isVisible={showLabValueSidebar}
                  onSIColumnChange={handleSIColumnChange}
                />
              </div>
            )}

            {/* Lab Value Sidebar (mobile/tablet overlay only) */}
            {showLabValueSidebar && window.innerWidth < 1024 && (
              <div className="practice-test-sidebar-container pt-sidebar lab-value-sidebar-mobile" style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0',
                flexShrink: 0,
                marginLeft: 0,
                paddingLeft: 0,
                position: 'relative',
                width: window.innerWidth <= 768 ? '100%' : (labValueSISColumn ? '450px' : '400px'),
                maxWidth: window.innerWidth <= 768 ? 'none' : (labValueSISColumn ? '450px' : '400px'),
                flex: window.innerWidth <= 768 ? '1 1 auto' : '0 1 auto',
                minWidth: window.innerWidth <= 768 ? '0' : undefined,
                height: 'auto',
                maxHeight: window.innerWidth <= 768 ? '80vh' : '70vh',
                overflow: 'hidden',
                boxSizing: 'border-box',
                backgroundColor: styles.content.background,
                border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
                borderRadius: '12px',
                boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 16px rgba(0, 0, 0, 0.08)',
                padding: '0',
                margin: '0',
                transition: 'width 0.3s ease'
              }}>
                <LabValueSidebar
                  isDarkMode={isDarkMode}
                  themeStyles={styles}
                  toggleSidebar={() => setShowLabValueSidebar(false)}
                  isVisible={showLabValueSidebar}
                  onSIColumnChange={handleSIColumnChange}
                />
              </div>
            )}
          
          {/* Main question content */}          <div 
            id="examflip-question-container"
            ref={(el) => {
              questionContainerRef.current = el;
              if (el) {
                const rect = el.getBoundingClientRect();
                // console.log('[QuestionContainer] Ref callback:', {
                //   left: rect.left,
                //   top: rect.top,
                //   width: rect.width,
                //   height: rect.height,
                //   styleLeft: el.style.left,
                //   styleWidth: el.style.width,
                //   stylePosition: el.style.position,
                //   computedStyle: {
                //     left: window.getComputedStyle(el).left,
                //     position: window.getComputedStyle(el).position,
                //     width: window.getComputedStyle(el).width
                //   },
                //   showLabValueSidebar,
                //   isSidebarVisible,
                //   windowWidth: window.innerWidth
                // });
                
                const computedStyle = window.getComputedStyle(el);
                
                // Check for any transforms that might affect positioning
                if (computedStyle.transform && computedStyle.transform !== 'none') {
                  console.warn('[QuestionContainer] WARNING: Container has transform, this may affect absolute positioning:', computedStyle.transform);
                }
                
                // Check if container has relative positioning
                if (computedStyle.position !== 'relative' && computedStyle.position !== 'absolute') {
                  console.warn('[QuestionContainer] WARNING: Container does not have relative positioning, resize handle may not position correctly');
                }
              }
            }}
            className={`question-section pt-question resizable ${((!isSidebarVisible && !showLabValueSidebar) && window.innerWidth > 768) ? 'expanding' : ''}`} 
            style={{
            boxShadow: styles.content.shadow,
            padding: '12px',
            borderRadius: '12px',
            // Properly handle flex when sidebars are present
            flex: (showLabValueSidebar && window.innerWidth >= 1024) || (isSidebarVisible && window.innerWidth > 768) ? '1 1 auto' : 'none',
            flexShrink: (showLabValueSidebar && window.innerWidth >= 1024) || (isSidebarVisible && window.innerWidth > 768) ? 1 : 0,
            minWidth: (showLabValueSidebar && window.innerWidth >= 1024) || (isSidebarVisible && window.innerWidth > 768) ? '400px' : 0,
            maxWidth: (isSidebarVisible && window.innerWidth > 768 && !showLabValueSidebar) ? 'calc(100% - 300px)' : 'none',
            // Let content define its height automatically
            height: 'auto',
            minHeight: '400px',
            maxHeight: 'none',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: styles.content.background,
            // Keep container consistently left-aligned regardless of sidebar state
            marginLeft: '0',
            marginRight: '0',
            // Removed transition to prevent any potential layout shifts
            // transition: 'margin-left 0.3s ease',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
            position: 'relative',
            left: 0, // Anchor to left side
            overflow: 'visible',
            boxSizing: 'border-box',
            // Set initial width if available, otherwise use default
            width: questionContainerWidth ? `${questionContainerWidth}px` : 'auto'
            // Removed contain: 'layout' as it can cause layout shift issues in some browsers
            // contain: 'layout'
            // Remove inline resize since using custom handle
            // resize: 'vertical'
          }}>
            {/* Progress indicator */}
            <ProgressIndicator 
              currentQuestionIndex={currentQuestionIndex}
              totalQuestions={questions.length}
              completedQuestions={completedCount}
              themeStyles={styles}
              isDarkMode={isDarkMode}
            />            {/* Session header - only show if there are systems or disciplines to display */}
            {(sessionSetup?.systems || sessionSetup?.disciplines) && (
              <SessionHeader 
                sessionSetup={sessionSetup}
                themeStyles={styles}
                isDarkMode={isDarkMode}
              />
            )}

            {/* Question content area */}            <QuestionContent
              currentQuestion={currentQuestion}
              currentAnswers={currentAnswers}
              selectedAnswer={selectedAnswer}
              setSelectedAnswer={setSelectedAnswer}
              isAnswerSubmitted={isAnswerSubmitted}
              setIsAnswerSubmitted={setIsAnswerSubmitted}
              isCorrect={isCorrect}
              setIsCorrect={setIsCorrect}
              isFlagged={examResponses[currentQuestion?.id]?.isFlagged || false}
              isLocked={isCurrentQuestionLocked(currentQuestion?.id)}
              themeStyles={styles}
              isDarkMode={isDarkMode}
              showClues={showClues}
              toggleClues={toggleClues}
              // Question Notes props
              showNotes={showNotes}
              handleToggleNotes={handleToggleNotes}
              currentQuestionNote={currentQuestionNote}
              handleNoteChange={handleNoteChange}
              handleNoteKeyDown={handleNoteKeyDown}
              handleNoteSave={handleNoteSave}
              isLoadingNote={isLoadingNote}
              isSavingNote={isSavingNote}
              hasUnsavedChanges={hasUnsavedChanges}
              noteLastSaved={noteLastSaved}
              noteError={noteError}
              // Question Assets props
              currentQuestionAssets={currentQuestionAssets}
              isLoadingAssets={isLoadingAssets}
              assetsError={assetsError}
            >
              {/* Answer options rendered as children of QuestionContent */}              
              {/* {console.log(`[PracticeTest][${componentIdRef.current}] Rendering CleanAnswerOptions with questionId: ${currentQuestion?.id}`, {
                currentAnswersLength: currentAnswers?.length || 0,
                answersSource: currentAnswers ? 'From parent state' : 'Not available',
                renderTimestamp: new Date().toISOString(),
                parentRenderTime: renderInfo.timestamp,
                parentRenderCount: renderInfo.renderCount,
                questionRef: currentQuestion ? JSON.stringify(Object.keys(currentQuestion)) : 'null'
              })} */}
              {/* {(!CleanAnswerOptions) && console.error('[PracticeTest] CleanAnswerOptions is undefined at render time')} */}
              {CleanAnswerOptions && (
              <CleanAnswerOptions
                ref={cleanAnswerOptionsRef}
                key={`question-${currentQuestion?.id}`}
                currentQuestion={currentQuestion}
                selectedAnswer={selectedAnswer}
                setSelectedAnswer={setSelectedAnswer}
                isAnswerSubmitted={isAnswerSubmitted}
                // setIsAnswerSubmitted is handled by onAnswerSubmitted callback
                isCorrect={isCorrect}
                // setIsCorrect is handled by onAnswerSubmitted callback
                isFlagged={examResponses[currentQuestion?.id]?.isFlagged || false}
                // isFlagged is handled by parent toggleFlag
                isLocked={isCurrentQuestionLocked(currentQuestion?.id)}
                themeStyles={styles}
                isDarkMode={isDarkMode}
                examData={examData} // Pass examData for end date checking
                onAnswerSubmitted={handleAnswerSubmitted} // This will set isAnswerSubmitted and isCorrect
                onAnswersLoaded={handleAnswersLoaded}
                submitTrigger={submitTrigger} // Trigger for external submit
                hideButtons={true} // Hide CleanAnswerOptions' own buttons since we have unified controls
                reviewMode={examData?.status === 'Finished' || shouldPreventBackendUpdates(examData)}
                // onNavigateNext={goToNextQuestion} // Next is handled by the main PracticeTest buttons now
                // onSkipQuestion={handleSkipQuestion} // Skip is handled by the main PracticeTest buttons now
              />)}

              {/* Finished Exam Status Message */}
              {examData && examData.status === 'Finished' && (
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: isDarkMode ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
                  borderLeft: `4px solid ${isDarkMode ? '#22c55e' : '#16a34a'}`,
                  borderRadius: '0 6px 6px 0',
                  textAlign: 'center'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: isDarkMode ? '#22c55e' : '#16a34a',
                    marginBottom: '5px'
                  }}>
                    <FontAwesomeIcon icon={faFlag} />
                    Exam Completed
                  </div>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    color: isDarkMode ? '#e2e8f0' : '#475569'
                  }}>
                    This exam has been finished. You can navigate between questions to review your answers, but no further changes can be made.
                  </p>
                </div>
              )}              {/* Redesigned Button Layout: Icons on Left, Navigation on Right */}
              <div className="question-controls" style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px',
                marginTop: '8px', // reduced to place controls closer to answers
                padding: '8px 0 0 0', // remove extra padding that added vertical space
                borderTop: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.05)',
                backgroundColor: 'transparent'
              }}>
                {/* Left Side: Flag and Feedback Icons */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  {/* Flag Icon Button */}
                  <button
                    className={`icon-button ${examResponses[currentQuestion?.id]?.isFlagged ? 'flagged' : ''}`}
                    onClick={() => {
                      // Disable flag functionality when exam is finished
                      if (examData?.status === 'Finished') return;
                      
                      const questionId = currentQuestion?.id;
                      if (questionId) {
                        const currentResponseData = examResponses[questionId] || {};
                        const newFlaggedState = !currentResponseData.isFlagged;
                        
                        // Update both centralized state AND local isFlagged state
                        updateExamResponse(questionId, {
                          ...currentResponseData,
                          isFlagged: newFlaggedState,
                          answerIds: currentResponseData.answerIds || [],
                          isCorrect: currentResponseData.isCorrect || false,
                          isSkipped: currentResponseData.isSkipped || false,
                          timeSpent: currentResponseData.timeSpent || 0,
                          timestamp: currentResponseData.timestamp || new Date().toISOString()
                        });
                        
                        // CRITICAL FIX: Also update the local isFlagged state for handleSkipQuestion
                        setIsFlagged(newFlaggedState);
                      }
                    }}
                    disabled={examData?.status === 'Finished'}
                    data-tooltip={examData?.status === 'Finished' ? 'Exam finished - cannot modify flags' : 
                           (examResponses[currentQuestion?.id]?.isFlagged ? 'Unflag this question' : 'Flag this question for review')}
                  >
                    <FontAwesomeIcon icon={faFlag} />
                  </button>

                  {/* Feedback Icon Button */}
                  <button
                    className="icon-button feedback-button"
                    onClick={() => setShowFeedbackModal(true)}
                    data-tooltip="Provide feedback about this question"
                  >
                    <FontAwesomeIcon icon={faComment} />
                  </button>
                  
                  {/* Folder Icon Button */}
                  <button
                    className="icon-button folder-button"
                    onClick={handleToggleFolderModal}
                    data-tooltip="Manage Folders / Add to Folder"
                  >
                    <FontAwesomeIcon icon={faFolder} />
                  </button>
                  
                  {/* Calculator Icon Button */}
                  <button
                    className="icon-button calculator-button"
                    onClick={() => setShowCalculator(true)}
                    data-tooltip="Open Calculator"
                  >
                    <FontAwesomeIcon icon={faCalculator} />
                  </button>

                  {/* Create Flashcard Icon Button */}
                  <button
                    className="icon-button create-flashcard-button"
                    onClick={() => setShowCreateFlashcardModal(true)}
                    data-tooltip="Create Flashcard"
                  >
                    <FontAwesomeIcon icon={faBookOpen} />
                  </button>

                  {/* Ask AI Button */}
                  <button
                    className="ai-chat-button"
                    onClick={handleAIChatOpen}
                    title="Ask AI about this question"
                    style={{ marginTop: 0 }}
                  >
                    <FontAwesomeIcon icon={faRobot} style={{ marginRight: isMobileView ? '0' : '6px' }} />
                    {!isMobileView && 'Ask AI'}
                  </button>
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  {/* Review mode navigation (finished or updates prevented): Always show Prev/Next */}
                  {(examData?.status === 'Finished' || shouldPreventBackendUpdates(examData)) ? (
                    <>
                      {currentQuestionIndex > 0 && (
            <button 
                          onClick={goToPrevQuestion}
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '10px 15px',
                            borderRadius: '6px',
                            backgroundColor: isDarkMode ? styles.button.secondary.background : styles.button.secondary.background,
                            color: isDarkMode ? styles.button.secondary.text : styles.button.secondary.text,
                            border: 'none',
                            cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginTop: 0,
                            fontWeight: 500
                          }}
                        >
                          <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '8px' }} />
                          Previous
                        </button>
                      )}

            <button 
                        onClick={goToNextQuestion}
                        disabled={isLastQuestion}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 20px',
                          borderRadius: '6px',
                          backgroundColor: isLastQuestion ? (isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.1)') : (isDarkMode ? styles.button.primary.background : styles.button.primary.background),
                          color: isLastQuestion ? (isDarkMode ? '#64748b' : '#94a3b8') : (isDarkMode ? styles.button.primary.text : styles.button.primary.text),
                          border: 'none',
                          cursor: isLastQuestion ? 'not-allowed' : 'pointer',
                          opacity: isLastQuestion ? 0.5 : 1,
              transition: 'all 0.2s ease',
              marginTop: 0,
                          fontWeight: 600
                        }}
                      >
                        Next <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '8px' }} />
                      </button>
                    </>
                  ) : (
                    <>
                  {/* Previous Button - Hidden on first question */}
                  {currentQuestionIndex > 0 && (
          <button 
                      onClick={goToPrevQuestion}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        padding: '10px 15px',
                        borderRadius: '6px',
                        backgroundColor: isDarkMode ? styles.button.secondary.background : styles.button.secondary.background,
                        color: isDarkMode ? styles.button.secondary.text : styles.button.secondary.text,
                        border: 'none',
                        cursor: 'pointer',
            transition: 'all 0.2s ease',
            marginTop: 0,
                        fontWeight: 500
                      }}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '8px' }} />
                      Previous
                    </button>
                  )}

                  {/* Skip Button - Only visible before answer submission and not locked */}
                  {!isAnswerSubmitted && !isCurrentQuestionLocked(currentQuestion?.id) && (
          <button
                      onClick={handleSkipQuestion}
                      disabled={examData?.status === 'Finished' || (isLastQuestion && currentQuestionIndex >= questions.length - 1)}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '6px',
                        backgroundColor: (examData?.status === 'Finished' || (isLastQuestion && currentQuestionIndex >= questions.length - 1)) ? 
                          (isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.1)') :
                          (isDarkMode ? styles.button.secondary.background : styles.button.secondary.background),
                        color: (examData?.status === 'Finished' || (isLastQuestion && currentQuestionIndex >= questions.length - 1)) ? 
                          (isDarkMode ? '#64748b' : '#94a3b8') :
                          (isDarkMode ? styles.button.secondary.text : styles.button.secondary.text),
                        border: 'none',
                        cursor: (examData?.status === 'Finished' || (isLastQuestion && currentQuestionIndex >= questions.length - 1)) ? 'not-allowed' : 'pointer',
            opacity: (examData?.status === 'Finished' || (isLastQuestion && currentQuestionIndex >= questions.length - 1)) ? 0.5 : 1,
            transition: 'all 0.2s ease',
            marginTop: 0,
                        fontWeight: 500
                      }}
                    >
                      Skip
                    </button>
                  )}

                  {/* Submit Answer Button - Transforms to Next Question after submission */}
                  {!isAnswerSubmitted && !isCurrentQuestionLocked(currentQuestion?.id) ? (
                    <button
                      onClick={() => {
                        // Don't allow submission when exam is finished
                        if (examData?.status === 'Finished') return;
                        
                        // Trigger CleanAnswerOptions submission via ref or direct call
                        if (cleanAnswerOptionsRef.current && cleanAnswerOptionsRef.current.submitAnswer) {
                          cleanAnswerOptionsRef.current.submitAnswer();
                        } else {
                          // console.log("Submit Answer button clicked - CleanAnswerOptions should handle submission via internal trigger");
                          // Fallback: Directly trigger submission if ref method not available
                          // We'll need to pass a submit trigger to CleanAnswerOptions
                          setSubmitTrigger(prev => prev + 1);
                        }
                      }}
                      disabled={!selectedAnswer || isCurrentQuestionLocked(currentQuestion?.id) || examData?.status === 'Finished'}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '6px',
                        backgroundColor: (!selectedAnswer || isCurrentQuestionLocked(currentQuestion?.id) || examData?.status === 'Finished') ?
                          (isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.1)') :
                          (isDarkMode ? styles.button.primary.background : styles.button.primary.background),
                        color: (!selectedAnswer || isCurrentQuestionLocked(currentQuestion?.id) || examData?.status === 'Finished') ?
                          (isDarkMode ? '#64748b' : '#94a3b8') :
                          (isDarkMode ? styles.button.primary.text : styles.button.primary.text),
                        border: 'none',
                        cursor: (!selectedAnswer || isCurrentQuestionLocked(currentQuestion?.id) || examData?.status === 'Finished') ? 'not-allowed' : 'pointer',
                        opacity: (!selectedAnswer || isCurrentQuestionLocked(currentQuestion?.id) || examData?.status === 'Finished') ? 0.5 : 1,
                        transition: 'all 0.2s ease',
                        marginTop: 0,
                        fontWeight: 600
                      }}
                    >
                      Submit Answer
                    </button>
                  ) : (
                    // Next Question Button (replaces Submit Answer after submission)
                    !isLastQuestion ? (
            <button 
                        onClick={goToNextQuestion}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 20px',
                          borderRadius: '6px',
                          backgroundColor: isDarkMode ? styles.button.primary.background : styles.button.primary.background,
                          color: isDarkMode ? styles.button.primary.text : styles.button.primary.text,
                          border: 'none',
                          cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginTop: 0,
                          fontWeight: 600
                        }}
                      >
                        Next Question
                        <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '8px' }} />
                      </button>
                    ) : (
                      // Last question after submission: show disabled Submit Answer (session already ended elsewhere)
            <button 
                        disabled
                        style={{ 
                          padding: '10px 20px',
                          borderRadius: '6px',
                          backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.1)',
                          color: isDarkMode ? '#64748b' : '#94a3b8',
                          border: 'none',
                          cursor: 'not-allowed',
                          opacity: 0.5,
              transition: 'all 0.2s ease',
              marginTop: 0,
                          fontWeight: 600
                        }}
                      >
                        Submit Answer
                      </button>
                    )
                  )}
                    </>
                  )}
                </div>
              </div>
            </QuestionContent>

            {/* Custom Resize Handle */}
            <div 
              className="resize-handle"
              ref={(el) => {
                if (el) {
                  const rect = el.getBoundingClientRect();
                  
                  const computedStyle = window.getComputedStyle(el);
                  
                  // Check parent container
                  const parent = el.parentElement;
                  if (parent) {
                    const parentRect = parent.getBoundingClientRect();
                    
                    const parentStyle = window.getComputedStyle(parent);
                    
                    // Check if parent has transform
                    if (parentStyle.transform && parentStyle.transform !== 'none') {
                      console.warn('[ResizeHandle] WARNING: Parent has transform, this may affect absolute positioning:', parentStyle.transform);
                    }
                  }
                }
              }}
              style={{
                position: 'absolute',
                bottom: '4px',
                right: '4px',
                width: '24px',
                height: '24px',
                cursor: 'ew-resize',
                background: isDarkMode 
                  ? 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' 
                  : 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                border: `2px solid ${isDarkMode ? '#4c1d95' : '#6b21a8'}`,
                borderRadius: '0 0 8px 0',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                pointerEvents: 'auto',
                opacity: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: 'white',
                fontWeight: 'bold',
                clipPath: 'polygon(0 100%, 100% 0, 100% 100%)',
                transition: 'all 0.2s ease'
              }}
              onMouseDown={(e) => {
                // Only allow resizing on desktop screens (1025px and above)
                if (window.innerWidth < 1025) {
                  return; // Disable resize on mobile/tablet
                }
                
                e.preventDefault();
                const startX = e.clientX;
                const startWidth = questionContainerRef.current.offsetWidth;
                
                const handleMouseMove = (moveEvent) => {
                  const deltaX = moveEvent.clientX - startX;
                  const newWidth = startWidth + deltaX;
                  
                  // Apply minimum and maximum constraints
                  const minWidth = 600; // Minimum width to maintain readability
                  const sidebarSpace = (showLabValueSidebar && window.innerWidth >= 1024) ? 800 : 400; // Account for lab values sidebar
                  const maxWidth = Math.max(1200, window.innerWidth - sidebarSpace); // Maximum width based on viewport, leaving space for sidebars
                  
                  if (newWidth >= minWidth && newWidth <= maxWidth) {
                    // console.log('[ResizeHandle] Applying resize:', {
                    //   deltaX,
                    //   newWidth,
                    //   startWidth,
                    //   beforeRect: questionContainerRef.current?.getBoundingClientRect(),
                    //   beforeStyle: {
                    //     left: questionContainerRef.current?.style.left,
                    //     width: questionContainerRef.current?.style.width
                    //   }
                    // });
                    
                    questionContainerRef.current.style.setProperty('width', `${newWidth}px`, 'important');
                    questionContainerRef.current.style.setProperty('max-width', `${newWidth}px`, 'important');
                    // Ensure left alignment is maintained
                    questionContainerRef.current.style.setProperty('left', '0px', 'important');
                    
                    // console.log('[ResizeHandle] After resize:', {
                    //   afterRect: questionContainerRef.current?.getBoundingClientRect(),
                    //   afterStyle: {
                    //     left: questionContainerRef.current?.style.left,
                    //     width: questionContainerRef.current?.style.width
                    //   }
                    // });
                  }
                };
                
                const handleMouseUp = () => {
                  const finalWidth = questionContainerRef.current.offsetWidth;
                  
                  setQuestionContainerWidth(finalWidth);
                  
                  localStorage.setItem('examflip-question-container-width', finalWidth.toString());
                  
                  // Update viewport state after manual resize to prevent auto-reset
                  lastViewportStateRef.current = {
                    width: window.innerWidth,
                    height: window.innerHeight,
                    devicePixelRatio: window.devicePixelRatio || 1
                  };
                  
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                };
                
                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
              }}
            >
              ⋮⋮
            </div>
          </div>
        </div>
        </div>
      )}
      
      {/* Handle case when questions are loaded but currentQuestion is still loading */}
      {(setupComplete || localSetupComplete) && !isLoading && !error && questions.length > 0 && !currentQuestion && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          width: '100%',
          maxWidth: '800px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px'
          }}>
            <FontAwesomeIcon 
              icon={faSpinner} 
              spin 
              style={{ 
                marginRight: '10px', 
                fontSize: '24px',
                color: styles.text.primary 
              }} 
            />
            <h2 style={{ color: styles.text.primary, margin: 0 }}>Loading current question...</h2>
          </div>
          <p style={{ color: styles.text.secondary }}>
            Please wait while we prepare your question.
          </p>
        </div>
      )}
      
      {/* Handle case when there are no questions */}
      {(setupComplete || localSetupComplete) && !isLoading && !error && (!questions || questions.length === 0) && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          width: '100%',
          maxWidth: '800px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <h2 style={{ marginBottom: '20px' }}>No questions available</h2>
          <p style={{ marginBottom: '30px', color: styles.text.secondary }}>
            No questions match your selected criteria. Please try different filters or create a new session.
          </p>
          <button onClick={() => window.location.reload()} style={{
            padding: '10px 20px',
            borderRadius: '4px',
            backgroundColor: styles.button.primary.background,
            color: styles.button.primary.text,
            border: 'none',
            cursor: 'pointer',
            fontSize: '16px',
            transition: 'all 0.3s ease'
          }}>
            Start Over
          </button>
        </div>
      )}      {/* Feedback Modal */}
      <FeedbackModal
        showFeedbackModal={showFeedbackModal}
        setShowFeedbackModal={setShowFeedbackModal}
        onSubmitFeedback={handleFeedbackSubmit}
        currentQuestion={currentQuestion}
        themeStyles={styles}
        isDarkMode={isDarkMode}
      />

      {/* Add Debug Monitor for tracking clue state changes */}
      <DebugMonitor 
        showClues={showClues}
        currentQuestionId={currentQuestion?.id}
        enabled={false} // Set to false in production
      />
      {/* Folder Management Modal */}
      <FolderManagementModal
        showModal={showFolderModal}
        onClose={() => setShowFolderModal(false)}
        folders={userFolders}
        currentQuestion={currentQuestion}
        currentQuestionFolderIds={currentQuestionFolderIds}
        onCreateFolder={handleCreateFolder}
        onUpdateFolder={handleUpdateFolder}
        onDeleteFolder={handleDeleteFolder}
        onAddQuestionToFolder={handleAddQuestionToFolder}
        onRemoveQuestionFromFolder={handleRemoveQuestionFromFolder}
        themeStyles={styles}
        isDarkMode={isDarkMode}
        isLoadingFolders={isLoadingFolders}
        folderError={folderError}
      />

      {/* Calculator Modal */}
      <Calculator
        isOpen={showCalculator}
        onClose={() => setShowCalculator(false)}
      />

      {/* Create Flashcard Modal */}
      {showCreateFlashcardModal && currentQuestion && (
        <CreateFlashcardModal
          question={{ ...currentQuestion, answers: currentAnswers }}
          onClose={() => setShowCreateFlashcardModal(false)}
        />
      )}

      {/* AI Chat Modal */}
      <QuestionAIChat
        ref={aiChatRef}
        questionId={currentQuestion?.id}
        examId={examData?.id}
        isOpen={showAIChat}
        onClose={handleAIChatClose}
        isDarkMode={isDarkMode}
        themeStyles={styles}
      />

      {/* End Session Confirmation Modal */}
      {showEndSessionModal && (
        <div className="modal-overlay" onClick={handleCancelEndSession}>
          <div className="end-session-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>End Session</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to end this exam session?</p>
              <div className="warning-message">
                <span className="warning-icon">⚠️</span>
                <span>This action cannot be undone. Your progress will be saved.</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCancelEndSession}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleConfirmEndSession}>
                End Session
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(PracticeTest);
